import type { FishIQSpeciesImage } from '../types';

/**
 * Audited FishIQ visual-reference manifest — Roddo verified pack v2 (USFWS + SoCal marine).
 *
 * IMPORTANT: upload the matching storage/ folder before swapping this file into Roddo.
 * Display eligibility and scanner eligibility remain intentionally separate.
 */

export const FISHIQ_SPECIES_IMAGES: readonly FishIQSpeciesImage[] = [
  {
    id: "usfws-11810",
    canonicalSpeciesId: "largemouth_bass",
    scientificName: "Micropterus salmoides",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flargemouth_bass%2Fusfws-11810%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flargemouth_bass%2Fusfws-11810%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/largemouth-bass-4",
    photographer: "Pos, Robert/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/largemouth-bass-4",
    creditText: "Pos, Robert/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clean lateral underwater/reference view. Strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2024-03-4/11810.jpg",
  },
  {
    id: "usfws-32912",
    canonicalSpeciesId: "smallmouth_bass",
    scientificName: "Micropterus dolomieu",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_bass%2Fusfws-32912%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_bass%2Fusfws-32912%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/smallmouth-bass-29",
    photographer: "Hagerty, Ryan/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/smallmouth-bass-29",
    creditText: "Hagerty, Ryan/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Underwater habitat image; useful for display but fish is not isolated enough for scanner evidence in this first pack. Source original: https://www.fws.gov/sites/default/files/images/2019-07/32912.jpg",
  },
  {
    id: "usfws-30687",
    canonicalSpeciesId: "rainbow_trout",
    scientificName: "Oncorhynchus mykiss",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_trout%2Fusfws-30687%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_trout%2Fusfws-30687%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/rainbow-trout-29",
    photographer: "Hagerty, Ryan/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/rainbow-trout-29",
    creditText: "Hagerty, Ryan/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clear underwater side profile; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2024-03-6/30687.jpg",
  },
  {
    id: "usfws-3746",
    canonicalSpeciesId: "brown_trout",
    scientificName: "Salmo trutta",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrown_trout%2Fusfws-3746%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrown_trout%2Fusfws-3746%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/brown-trout-1",
    photographer: "Engbretson, Eric",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/brown-trout-1",
    creditText: "Engbretson, Eric",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Close-up head image. Display/reference useful; not a full-body scanner reference. Source original: https://www.fws.gov/sites/default/files/images/2024-03-2/3746.jpg",
  },
  {
    id: "usfws-31794",
    canonicalSpeciesId: "channel_catfish",
    scientificName: "Ictalurus punctatus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchannel_catfish%2Fusfws-31794%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchannel_catfish%2Fusfws-31794%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/channel-catfish-3",
    photographer: "Stukel, Sam/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/channel-catfish-3",
    creditText: "Stukel, Sam/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clean lateral profile; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2019-02/31794.jpg",
  },
  {
    id: "usfws-26202",
    canonicalSpeciesId: "blue_catfish",
    scientificName: "Ictalurus furcatus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_catfish%2Fusfws-26202%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_catfish%2Fusfws-26202%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/blue-catfish-0",
    photographer: "Billings, Brett/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/blue-catfish-0",
    creditText: "Billings, Brett/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Aquarium reference with identifiable body profile; scanner-eligible after visual audit. Source original: https://www.fws.gov/sites/default/files/images/2017-05/26202.jpg",
  },
  {
    id: "usfws-21016",
    canonicalSpeciesId: "common_carp",
    scientificName: "Cyprinus carpio",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_carp%2Fusfws-21016%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_carp%2Fusfws-21016%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/common-carp",
    photographer: "Patterson, Matthew/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/common-carp",
    creditText: "Patterson, Matthew/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "NCTC teaching-collection specimen. Excellent morphology reference, but preserved coloration makes it display-only for now. Source original: https://www.fws.gov/sites/default/files/images/2016-01/21016.jpg",
  },
  {
    id: "usfws-31861",
    canonicalSpeciesId: "bluegill",
    scientificName: "Lepomis macrochirus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluegill%2Fusfws-31861%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluegill%2Fusfws-31861%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/bluegill-8",
    photographer: "Stukel, Sam/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/bluegill-8",
    creditText: "Stukel, Sam/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clear full-body reference; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2018-02/31861.jpg",
  },
  {
    id: "usfws-31913",
    canonicalSpeciesId: "black_crappie",
    scientificName: "Pomoxis nigromaculatus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_crappie%2Fusfws-31913%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_crappie%2Fusfws-31913%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/black-crappie-5",
    photographer: "Stukel, Sam/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/black-crappie-5",
    creditText: "Stukel, Sam/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clean full-body lateral reference; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2018-01/31913.jpg",
  },
  {
    id: "usfws-2810",
    canonicalSpeciesId: "white_crappie",
    scientificName: "Pomoxis annularis",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_crappie%2Fusfws-2810%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_crappie%2Fusfws-2810%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/white-crappie",
    photographer: "Raver, Duane",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/white-crappie",
    creditText: "Raver, Duane",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Public-domain FWS commissioned artwork. Good display reference; excluded from photo scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2024-03-2/2810.jpg",
  },
  {
    id: "usfws-25683",
    canonicalSpeciesId: "striped_bass",
    scientificName: "Morone saxatilis",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_bass%2Fusfws-25683%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_bass%2Fusfws-25683%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/striped-bass-10",
    photographer: "Hagerty, Ryan/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/striped-bass-10",
    creditText: "Hagerty, Ryan/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clear lateral profile; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2017-03/25683.jpg",
  },
  {
    id: "usfws-img1059",
    canonicalSpeciesId: "walleye",
    scientificName: "Sander vitreus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalleye%2Fusfws-img1059%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalleye%2Fusfws-img1059%2Fthumb.webp?alt=media",
    angle: "caught",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/walleye-caught-upper-miss-river",
    photographer: "USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/walleye-caught-upper-miss-river",
    creditText: "USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Full-body field/caught reference with visible diagnostic features; scanner-eligible. Source original: https://www.fws.gov/sites/default/files/2023-08/IMG_1059%20%282%29.JPG",
  },
  {
    id: "usfws-26976",
    canonicalSpeciesId: "northern_pike",
    scientificName: "Esox lucius",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_pike%2Fusfws-26976%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_pike%2Fusfws-26976%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/northern-pike-8",
    photographer: "Hagerty, Ryan/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/northern-pike-8",
    creditText: "Hagerty, Ryan/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clear underwater lateral view; strong scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/images/2024-03-6/26976.jpg",
  },
  {
    id: "usfws-hagerty",
    canonicalSpeciesId: "chinook_salmon",
    scientificName: "Oncorhynchus tshawytscha",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchinook_salmon%2Fusfws-hagerty%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchinook_salmon%2Fusfws-hagerty%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/chinook",
    photographer: "Ryan Hagerty/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/chinook",
    creditText: "Ryan Hagerty/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Clear spawning-adult lateral view; strong seasonal/adult scanner reference candidate. Source original: https://www.fws.gov/sites/default/files/banner_images/2021-08/Chinook-salmon-Ryan-Hagerty-usfws.jpg",
  },
  {
    id: "usfws-tabor",
    canonicalSpeciesId: "coho_salmon",
    scientificName: "Oncorhynchus kisutch",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoho_salmon%2Fusfws-tabor%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoho_salmon%2Fusfws-tabor%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/coho-salmon-7",
    photographer: "Roger Tabor/USFWS",
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/coho-salmon-7",
    creditText: "Roger Tabor/USFWS",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Underwater group image. Display/reference useful, but not isolated enough for scanner evidence. Source original: https://www.fws.gov/sites/default/files/banner_images/2022-07/coho-salmon.jpg",
  },
  {
    id: 'noaa-halibut-300',
    canonicalSpeciesId: 'halibut',
    scientificName: 'Paralichthys californicus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhalibut%2Fnoaa-halibut-300%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhalibut%2Fnoaa-halibut-300%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhalibut%2Fnoaa-halibut-300%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhalibut%2Fnoaa-halibut-300%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Halibut_300.jpg',
    photographer: 'Shane Anderson / NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Halibut_300.jpg',
    creditText: 'Shane Anderson / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NOAA public-domain California halibut image. Resized/re-encoded to WebP for Roddo; morphology and camouflage remain visible.',
  },
  {
    id: 'usfda-usnm-438103',
    canonicalSpeciesId: 'pacific_mackerel',
    scientificName: 'Scomber japonicus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_mackerel%2Fusfda-usnm-438103%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_mackerel%2Fusfda-usnm-438103%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_mackerel%2Fusfda-usnm-438103%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_mackerel%2Fusfda-usnm-438103%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Food and Drug Administration / Smithsonian NMNH',
    sourceName: 'FDA seafood identification reference via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Scomber_japonicus_(USNM-438103).jpg',
    photographer: 'Jeffrey Williams, National Museum of Natural History, Smithsonian Institution',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Scomber_japonicus_(USNM-438103).jpg',
    creditText: 'Jeffrey Williams / Smithsonian NMNH; U.S. FDA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Voucher-specimen side profile. Source states the photo is free of copyright restrictions; FDA government-work/public-domain status verified. Resized/re-encoded to WebP for Roddo.',
  },
  {
    id: 'commons-nickw557-2016',
    canonicalSpeciesId: 'barred_sand_bass',
    scientificName: 'Paralabrax nebulifer',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_sand_bass%2Fcommons-nickw557-2016%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_sand_bass%2Fcommons-nickw557-2016%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_sand_bass%2Fcommons-nickw557-2016%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_sand_bass%2Fcommons-nickw557-2016%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:P_nebulifer.jpg',
    photographer: 'NickW557',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'NickW557 / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Full-body Barred Sand Bass reference. CC BY-SA 4.0. Roddo WebP derivatives are resized/re-encoded versions and remain under CC BY-SA 4.0.',
  },
  {
    id: 'commons-basilosauridae-2018',
    canonicalSpeciesId: 'california_corbina',
    scientificName: 'Menticirrhus undulatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcalifornia_corbina%2Fcommons-basilosauridae-2018%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcalifornia_corbina%2Fcommons-basilosauridae-2018%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcalifornia_corbina%2Fcommons-basilosauridae-2018%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcalifornia_corbina%2Fcommons-basilosauridae-2018%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:CACorbina.jpg',
    photographer: 'Basilosauridae',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Basilosauridae / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Wild California corbina photographed at La Jolla Shores. CC BY-SA 4.0. Useful display/alternate-angle reference; held out of scanner evidence because the top/angled in-habitat view is not an ideal isolated morphology reference. Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-zoefricker-2024',
    canonicalSpeciesId: 'yellowtail_amberjack_pacific',
    scientificName: 'Seriola dorsalis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack_pacific%2Fcommons-zoefricker-2024%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack_pacific%2Fcommons-zoefricker-2024%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack_pacific%2Fcommons-zoefricker-2024%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack_pacific%2Fcommons-zoefricker-2024%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:California_yellowtail.jpg',
    photographer: 'Zoefricker',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Zoefricker / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'California yellowtail in kelp, front-left lateral view. CC BY-SA 4.0. Roddo WebP derivatives are resized/re-encoded versions and remain under CC BY-SA 4.0.',
  },
  {
    id: 'usfws-31900',
    canonicalSpeciesId: 'yellow_perch',
    scientificName: 'Perca flavescens',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellow_perch%2Fusfws-31900%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellow_perch%2Fusfws-31900%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellow_perch%2Fusfws-31900%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellow_perch%2Fusfws-31900%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/yellow-perch-3',
    photographer: 'Stukel, Sam/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/yellow-perch-3',
    creditText: 'Stukel, Sam/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clean isolated full-body lateral reference on a neutral background. Source original: https://www.fws.gov/sites/default/files/images/2018-02/31900.jpg',
  },
  {
    id: 'usfws-26000',
    canonicalSpeciesId: 'brook_trout',
    scientificName: 'Salvelinus fontinalis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrook_trout%2Fusfws-26000%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrook_trout%2Fusfws-26000%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrook_trout%2Fusfws-26000%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbrook_trout%2Fusfws-26000%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/brook-trout-19',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/brook-trout-19',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Verified hatchery/aquarium group image; useful display reference but multiple fish make it poor scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2017-02/26000.jpg',
  },
  {
    id: 'usfws-c88a4828',
    canonicalSpeciesId: 'lake_trout',
    scientificName: 'Salvelinus namaycush',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_trout%2Fusfws-c88a4828%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_trout%2Fusfws-c88a4828%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_trout%2Fusfws-c88a4828%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_trout%2Fusfws-c88a4828%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/lake-trout-4',
    photographer: 'Ryan Hagerty/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/lake-trout-4',
    creditText: 'Ryan Hagerty/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Natural underwater image with a strong foreground lake trout, but multiple fish are present; display-only for scanner gating. Source original: https://www.fws.gov/sites/default/files/2023-10/C88A4828.jpg',
  },
  {
    id: 'usfws-4755',
    canonicalSpeciesId: 'cutthroat_trout',
    scientificName: 'Oncorhynchus clarkii',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcutthroat_trout%2Fusfws-4755%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcutthroat_trout%2Fusfws-4755%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcutthroat_trout%2Fusfws-4755%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcutthroat_trout%2Fusfws-4755%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/cutthroat-trout-1',
    photographer: 'Johnson, James E.',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/cutthroat-trout-1',
    creditText: 'Johnson, James E.',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clear full-body specimen/catch reference with natural coloration; held out of scanner evidence because of specimen-board context. Source original: https://www.fws.gov/sites/default/files/images/2024-03-3/4755.jpg',
  },
  {
    id: 'usfws-bowfin-ryan-hagerty',
    canonicalSpeciesId: 'bowfin',
    scientificName: 'Amia calva',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbowfin%2Fusfws-bowfin-ryan-hagerty%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbowfin%2Fusfws-bowfin-ryan-hagerty%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbowfin%2Fusfws-bowfin-ryan-hagerty%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbowfin%2Fusfws-bowfin-ryan-hagerty%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/bowfin-ryan-hagertyjpg',
    photographer: 'Ryan Hagerty/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/bowfin-ryan-hagertyjpg',
    creditText: 'Ryan Hagerty/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clear natural underwater lateral profile. FWS species profile separately confirms Bowfin = Amia calva. Source original: https://www.fws.gov/sites/default/files/2022-07/bowfin%20ryan%20hagerty.jpg',
  },
  {
    id: 'usfws-32033',
    canonicalSpeciesId: 'white_bass',
    scientificName: 'Morone chrysops',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_bass%2Fusfws-32033%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_bass%2Fusfws-32033%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_bass%2Fusfws-32033%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_bass%2Fusfws-32033%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/white-bass-4',
    photographer: 'Stukel, Sam/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/white-bass-4',
    creditText: 'Stukel, Sam/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clean isolated full-body lateral reference on a neutral background. Source original: https://www.fws.gov/sites/default/files/images/2019-01/32033.jpg',
  },
  {
    id: 'usfws-c88a6813',
    canonicalSpeciesId: 'redear_sunfish',
    scientificName: 'Lepomis microlophus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredear_sunfish%2Fusfws-c88a6813%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredear_sunfish%2Fusfws-c88a6813%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredear_sunfish%2Fusfws-c88a6813%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredear_sunfish%2Fusfws-c88a6813%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/redear-sunfish',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/redear-sunfish',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Natural underwater group image; useful visual reference but multiple fish make it unsuitable as scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2024-05/c88a6813.jpg',
  },
  {
    id: 'usfws-32010',
    canonicalSpeciesId: 'freshwater_drum',
    scientificName: 'Aplodinotus grunniens',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffreshwater_drum%2Fusfws-32010%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffreshwater_drum%2Fusfws-32010%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffreshwater_drum%2Fusfws-32010%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffreshwater_drum%2Fusfws-32010%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/freshwater-drum-5',
    photographer: 'Stukel, Sam/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/freshwater-drum-5',
    creditText: 'Stukel, Sam/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clean isolated near-lateral morphology reference on a neutral background. Source original: https://www.fws.gov/sites/default/files/images/2019-02/32010.jpg',
  },
  {
    id: 'usfws-31891',
    canonicalSpeciesId: 'muskie',
    scientificName: 'Esox masquinongy',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmuskie%2Fusfws-31891%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmuskie%2Fusfws-31891%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmuskie%2Fusfws-31891%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmuskie%2Fusfws-31891%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/muskellunge-1',
    photographer: 'Sam Stukel/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/muskellunge-1',
    creditText: 'Sam Stukel/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clean isolated lateral morphology reference. Uses Roddo base-seed ID muskie, not regional alias muskellunge. Source original: https://www.fws.gov/sites/default/files/images/2018-02/31891.jpg',
  },
  {
    id: 'usfws-26851',
    canonicalSpeciesId: 'arctic_grayling',
    scientificName: 'Thymallus arcticus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_grayling%2Fusfws-26851%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_grayling%2Fusfws-26851%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_grayling%2Fusfws-26851%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_grayling%2Fusfws-26851%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/arctic-grayling-18',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/arctic-grayling-18',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Strong natural underwater lateral reference with distinctive dorsal profile; minor background fish do not obscure primary morphology. Source original: https://www.fws.gov/sites/default/files/images/2017-07/26851.jpg',
  },
  {
    id: 'usfws-21473',
    canonicalSpeciesId: 'spotted_bass',
    scientificName: 'Micropterus punctulatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bass%2Fusfws-21473%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bass%2Fusfws-21473%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bass%2Fusfws-21473%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bass%2Fusfws-21473%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/spotted-bass-0',
    photographer: 'Patterson, Matthew/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/spotted-bass-0',
    creditText: 'Patterson, Matthew/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NCTC teaching-collection specimen. Strong morphology reference, but preserved coloration makes it display-only. Source original: https://www.fws.gov/sites/default/files/images/2016-01/21473.jpg',
  },
  {
    id: 'usfws-25295',
    canonicalSpeciesId: 'flathead_catfish',
    scientificName: 'Pylodictis olivaris',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflathead_catfish%2Fusfws-25295%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflathead_catfish%2Fusfws-25295%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflathead_catfish%2Fusfws-25295%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflathead_catfish%2Fusfws-25295%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/flathead-catfish',
    photographer: 'Raver, Duane',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/flathead-catfish',
    creditText: 'Raver, Duane',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain FWS commissioned artwork. Excellent display morphology; excluded from photo scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2005-05/25295.jpg',
  },
  {
    id: 'usfws-25328',
    canonicalSpeciesId: 'chain_pickerel',
    scientificName: 'Esox niger',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchain_pickerel%2Fusfws-25328%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchain_pickerel%2Fusfws-25328%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchain_pickerel%2Fusfws-25328%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchain_pickerel%2Fusfws-25328%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/chain-pickerel',
    photographer: 'Raver, Duane',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/chain-pickerel',
    creditText: 'Raver, Duane',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain FWS commissioned artwork. Excellent display morphology; excluded from photo scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2005-05/25328.jpg',
  },
  {
    id: 'usfws-steelhead-c88a3170',
    canonicalSpeciesId: 'steelhead',
    scientificName: 'Oncorhynchus mykiss',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsteelhead%2Fusfws-steelhead-c88a3170%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsteelhead%2Fusfws-steelhead-c88a3170%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsteelhead%2Fusfws-steelhead-c88a3170%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsteelhead%2Fusfws-steelhead-c88a3170%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/steelhead-trout-0',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/steelhead-trout-0',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Clear natural underwater adult profile with diagnostic spotting and body form. Strong scanner evidence candidate. Source original: https://www.fws.gov/sites/default/files/styles/max_1300x1300/public/images/2022-04/Steelhead_C88A3170-Edit.jpg?itok=zvaFCSFU',
  },
  {
    id: 'commons-seb951-coregonus',
    canonicalSpeciesId: 'lake_whitefish',
    scientificName: 'Coregonus clupeaformis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_whitefish%2Fcommons-seb951-coregonus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_whitefish%2Fcommons-seb951-coregonus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_whitefish%2Fcommons-seb951-coregonus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flake_whitefish%2Fcommons-seb951-coregonus%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Coregonus_clupeaformis.jpg',
    photographer: 'Seb951 / Wikimedia Commons · CC BY-SA 3.0',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Seb951 / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Verified Lake Whitefish photo under CC BY-SA 3.0. Multiple fish are present, so this remains display-only. Source original: https://upload.wikimedia.org/wikipedia/commons/a/a9/Coregonus_clupeaformis.jpg',
  },
  {
    id: 'usfws-kokanee-koerner-2023',
    canonicalSpeciesId: 'kokanee_salmon',
    scientificName: 'Oncorhynchus nerka',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkokanee_salmon%2Fusfws-kokanee-koerner-2023%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkokanee_salmon%2Fusfws-kokanee-koerner-2023%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkokanee_salmon%2Fusfws-kokanee-koerner-2023%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkokanee_salmon%2Fusfws-kokanee-koerner-2023%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/kokanee-salmon-non-anadromous-form-sockeye-salmon-pacific-northwest',
    photographer: 'Tom Koerner/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/kokanee-salmon-non-anadromous-form-sockeye-salmon-pacific-northwest',
    creditText: 'Tom Koerner/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Verified Kokanee close-up. Strong seasonal display reference, but the crop does not show enough full-body morphology for scanner evidence. Source original: https://www.fws.gov/sites/default/files/2023-08/kokanee%20salmon%20landlocked%20version%20of%20sockeye%20salmon%20PNW%20%20tom%20koerner%20usfws.jpg',
  },
  {
    id: 'usfws-30645',
    canonicalSpeciesId: 'sockeye_salmon',
    scientificName: 'Oncorhynchus nerka',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsockeye_salmon%2Fusfws-30645%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsockeye_salmon%2Fusfws-30645%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsockeye_salmon%2Fusfws-30645%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsockeye_salmon%2Fusfws-30645%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/sockeye-salmon-104',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/sockeye-salmon-104',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Natural underwater spawning-group image. Excellent display/seasonal reference; multiple overlapping fish make it unsuitable as scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2018-09/30645.jpg',
  },
  {
    id: 'noaa-pink-salmon-2023',
    canonicalSpeciesId: 'pink_salmon',
    scientificName: 'Oncorhynchus gorbuscha',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpink_salmon%2Fnoaa-pink-salmon-2023%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpink_salmon%2Fnoaa-pink-salmon-2023%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpink_salmon%2Fnoaa-pink-salmon-2023%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpink_salmon%2Fnoaa-pink-salmon-2023%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library / NOAA',
    sourceUrl: 'https://www.fws.gov/media/pink-salmon-noaapng',
    photographer: 'NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/pink-salmon-noaapng',
    creditText: 'NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NOAA public-domain underwater Pink Salmon reference. Multiple fish and partial overlaps keep it display-only. Source original: https://www.fws.gov/sites/default/files/2023-08/pink-salmon-noaa.png',
  },
  {
    id: 'usfws-chum-tabor-2020',
    canonicalSpeciesId: 'chum_salmon',
    scientificName: 'Oncorhynchus keta',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchum_salmon%2Fusfws-chum-tabor-2020%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchum_salmon%2Fusfws-chum-tabor-2020%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchum_salmon%2Fusfws-chum-tabor-2020%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchum_salmon%2Fusfws-chum-tabor-2020%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/chum-salmon-allison-springs-wa',
    photographer: 'Roger Tabor/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/chum-salmon-allison-springs-wa',
    creditText: 'Roger Tabor/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Natural underwater spawning reference with visible adult morphology. Multiple fish are present, so display-only. Source original: https://www.fws.gov/sites/default/files/banner_images/2024-07/chum-salmon-roger-tabor-usfws.jpg',
  },
  {
    id: 'usfws-25429',
    canonicalSpeciesId: 'arctic_char',
    scientificName: 'Salvelinus alpinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fusfws-25429%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fusfws-25429%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fusfws-25429%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fusfws-25429%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/fisherman-catches-arctic-char',
    photographer: 'USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/fisherman-catches-arctic-char',
    creditText: 'USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: false,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Verified caught Arctic Char with clear coloration and body profile; human-hold context keeps it display-only for scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2024-03-6/25429.jpg',
  },
  {
    id: 'usfws-595',
    canonicalSpeciesId: 'speckled_trout',
    scientificName: 'Cynoscion nebulosus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspeckled_trout%2Fusfws-595%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspeckled_trout%2Fusfws-595%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspeckled_trout%2Fusfws-595%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspeckled_trout%2Fusfws-595%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/spotted-seatrout',
    photographer: 'Raver, Duane',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/spotted-seatrout',
    creditText: 'Raver, Duane',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain commissioned FWS artwork; strong display morphology reference, excluded from photo scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2024-03-1/595.jpg',
  },
  {
    id: 'noaa-centropomus-keys',
    canonicalSpeciesId: 'snook',
    scientificName: 'Centropomus undecimalis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnook%2Fnoaa-centropomus-keys%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnook%2Fnoaa-centropomus-keys%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnook%2Fnoaa-centropomus-keys%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnook%2Fnoaa-centropomus-keys%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Florida Keys National Marine Sanctuary',
    sourceName: 'NOAA via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Centropomus_undecimalis.jpg',
    photographer: 'Bob Care / Florida Keys NMS',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Centropomus_undecimalis.jpg',
    creditText: 'Bob Care / Florida Keys NMS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NOAA public-domain underwater snook image; useful display reference, but framing crops part of the body/tail so held out of scanner evidence. Source original: https://upload.wikimedia.org/wikipedia/commons/f/fc/Centropomus_undecimalis.jpg',
  },
  {
    id: 'commons-albert-kok-tarpon5',
    canonicalSpeciesId: 'tarpon',
    scientificName: 'Megalops atlanticus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon%2Fcommons-albert-kok-tarpon5%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon%2Fcommons-albert-kok-tarpon5%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon%2Fcommons-albert-kok-tarpon5%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon%2Fcommons-albert-kok-tarpon5%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Tarpon5.jpg',
    photographer: 'Albert kok / Wikimedia Commons · CC BY-SA 3.0',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Albert kok / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Natural underwater school with a strong foreground tarpon; CC BY-SA 3.0. Multiple fish make it display-only for scanner gating. Source original: https://upload.wikimedia.org/wikipedia/commons/e/e6/Tarpon5.jpg',
  },
  {
    id: 'commons-louisiana-seagrant-2010',
    canonicalSpeciesId: 'black_drum',
    scientificName: 'Pogonias cromis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_drum%2Fcommons-louisiana-seagrant-2010%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_drum%2Fcommons-louisiana-seagrant-2010%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_drum%2Fcommons-louisiana-seagrant-2010%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_drum%2Fcommons-louisiana-seagrant-2010%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Louisiana Sea Grant / Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Black_drum.jpg',
    photographer: 'Louisiana Sea Grant College Program / LSU · CC BY 2.0',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Louisiana Sea Grant College Program / LSU · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'High-detail black drum head/scale reference licensed CC BY 2.0; not full-body, so display-only for scanner evidence. Source original: https://upload.wikimedia.org/wikipedia/commons/c/c5/Black_drum.jpg',
  },
  {
    id: 'usfws-29092',
    canonicalSpeciesId: 'sheepshead',
    scientificName: 'Archosargus probatocephalus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fusfws-29092%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fusfws-29092%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fusfws-29092%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fusfws-29092%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/refuge-visitor-catches-sheepshead',
    photographer: 'Hillebrand, Steve/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/refuge-visitor-catches-sheepshead',
    creditText: 'Hillebrand, Steve/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: false,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Verified public-domain field/catch photo with diagnostic vertical bars; held out of scanner evidence because of human/field framing. Source original: https://www.fws.gov/sites/default/files/images/2009-02/29092.jpg',
  },
  {
    id: 'noaa-paralichthys-lethostigma',
    canonicalSpeciesId: 'flounder',
    scientificName: 'Paralichthys lethostigma',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflounder%2Fnoaa-paralichthys-lethostigma%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflounder%2Fnoaa-paralichthys-lethostigma%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflounder%2Fnoaa-paralichthys-lethostigma%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflounder%2Fnoaa-paralichthys-lethostigma%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS / SEFSC',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Paralichthys_lethostigma.jpg',
    photographer: 'SEFSC Pascagoula Laboratory / Brandi Noble / NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Paralichthys_lethostigma.jpg',
    creditText: 'SEFSC Pascagoula Laboratory / Brandi Noble / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NOAA public-domain isolated full-body Southern Flounder morphology reference; scanner-eligible. Source original: https://upload.wikimedia.org/wikipedia/commons/7/70/Paralichthys_lethostigma.jpg',
  },
  {
    id: 'usfda-fda058',
    canonicalSpeciesId: 'mangrove_snapper',
    scientificName: 'Lutjanus griseus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_snapper%2Fusfda-fda058%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_snapper%2Fusfda-fda058%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_snapper%2Fusfda-fda058%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_snapper%2Fusfda-fda058%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Food and Drug Administration',
    sourceName: 'FDA RSSL via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_griseus_(FDA_058).jpg',
    photographer: 'J. Deeds / U.S. Food and Drug Administration',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_griseus_(FDA_058).jpg',
    creditText: 'J. Deeds / U.S. Food and Drug Administration',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'FDA public-domain reference specimen with clear full-body lateral morphology; scanner-eligible. Source original: https://upload.wikimedia.org/wikipedia/commons/e/eb/Lutjanus_griseus_%28FDA_058%29.jpg',
  },
  {
    id: 'usfws-27682',
    canonicalSpeciesId: 'bluefish',
    scientificName: 'Pomatomus saltatrix',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefish%2Fusfws-27682%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefish%2Fusfws-27682%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefish%2Fusfws-27682%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefish%2Fusfws-27682%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/bluefish',
    photographer: 'Raver, Duane',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/bluefish',
    creditText: 'Raver, Duane',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain FWS commissioned artwork; excellent display morphology reference but excluded from photo scanner evidence. Source original: https://www.fws.gov/sites/default/files/images/2005-05/27682.jpg',
  },
  {
    id: 'noaa-centropristis-striata',
    canonicalSpeciesId: 'black_sea_bass',
    scientificName: 'Centropristis striata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_sea_bass%2Fnoaa-centropristis-striata%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_sea_bass%2Fnoaa-centropristis-striata%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_sea_bass%2Fnoaa-centropristis-striata%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_sea_bass%2Fnoaa-centropristis-striata%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS / SEFSC',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Centropristis_striata.jpg',
    photographer: 'SEFSC Pascagoula Laboratory / Brandi Noble / NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Centropristis_striata.jpg',
    creditText: 'SEFSC Pascagoula Laboratory / Brandi Noble / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-21',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'NOAA public-domain full-body reference with visible diagnostic morphology; scanner-eligible. Source original: https://upload.wikimedia.org/wikipedia/commons/0/0b/Centropristis_striata.jpg',
  },
  {
    id: 'commons-zouavman-arctic-char',
    canonicalSpeciesId: 'arctic_char',
    scientificName: 'Salvelinus alpinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fcommons-zouavman-arctic-char%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fcommons-zouavman-arctic-char%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fcommons-zouavman-arctic-char%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farctic_char%2Fcommons-zouavman-arctic-char%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Salvelinus_alpinus.JPG',
    photographer: 'Zouavman Le Zouave',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Zouavman Le Zouave / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Arctic Char aquarium/museum reference with a clear full-body lateral profile. Replaces the disabled human-in-frame reference for display. Roddo WebP derivatives remain CC BY-SA 3.0.',
  },
  {
    id: 'commons-jlikes2fish-pacific-halibut',
    canonicalSpeciesId: 'pacific_halibut',
    scientificName: 'Hippoglossus stenolepis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_halibut%2Fcommons-jlikes2fish-pacific-halibut%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_halibut%2Fcommons-jlikes2fish-pacific-halibut%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_halibut%2Fcommons-jlikes2fish-pacific-halibut%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_halibut%2Fcommons-jlikes2fish-pacific-halibut%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Pacific_Halibut.JPG',
    photographer: 'Jlikes2Fish',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Pacific_Halibut.JPG',
    creditText: 'Jlikes2Fish / Wikimedia Commons',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Pacific Halibut catch/specimen image from Cook Inlet, Alaska. Multiple fish and deck context make it display-only.',
  },
  {
    id: 'commons-ed-bierman-lingcod',
    canonicalSpeciesId: 'lingcod',
    scientificName: 'Ophiodon elongatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flingcod%2Fcommons-ed-bierman-lingcod%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flingcod%2Fcommons-ed-bierman-lingcod%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flingcod%2Fcommons-ed-bierman-lingcod%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flingcod%2Fcommons-ed-bierman-lingcod%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lingcod.jpg',
    photographer: 'Ed Bierman',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Ed Bierman / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Lingcod reference with strong diagnostic coloration and body form. Roddo WebP derivatives are resized/re-encoded and attributed under CC BY 2.0.',
  },
  {
    id: 'noaa-cinms-blue-rockfish',
    canonicalSpeciesId: 'blue_rockfish',
    scientificName: 'Sebastes mystinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_rockfish%2Fnoaa-cinms-blue-rockfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_rockfish%2Fnoaa-cinms-blue-rockfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_rockfish%2Fnoaa-cinms-blue-rockfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_rockfish%2Fnoaa-cinms-blue-rockfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Channel Islands National Marine Sanctuary',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Blue_rockfish_(Sebastes_mystinus)_CIMNS.jpg',
    photographer: 'Claire Fackler, CINMS, NOAA',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Claire Fackler / CINMS / NOAA · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Blue Rockfish reference. Clear lateral morphology; scanner-eligible. Commons presents the file under CC BY 2.0.',
  },
  {
    id: 'noaa-mbnms-cabezon',
    canonicalSpeciesId: 'cabezon',
    scientificName: 'Scorpaenichthys marmoratus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcabezon%2Fnoaa-mbnms-cabezon%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcabezon%2Fnoaa-mbnms-cabezon%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcabezon%2Fnoaa-mbnms-cabezon%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcabezon%2Fnoaa-mbnms-cabezon%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Monterey Bay National Marine Sanctuary',
    sourceName: 'SIMoN / MBNMS via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Scorpaenichthys_marmoratus.jpg',
    photographer: 'Steve Lonhart / SIMoN / MBNMS',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Scorpaenichthys_marmoratus.jpg',
    creditText: 'Steve Lonhart / SIMoN / MBNMS / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Cabezon reference. Strong head and pectoral detail, but framing is too close for full-body scanner evidence.',
  },
  {
    id: 'noaa-mbnms-vermilion-rockfish',
    canonicalSpeciesId: 'vermilion_rockfish',
    scientificName: 'Sebastes miniatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fvermilion_rockfish%2Fnoaa-mbnms-vermilion-rockfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fvermilion_rockfish%2Fnoaa-mbnms-vermilion-rockfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fvermilion_rockfish%2Fnoaa-mbnms-vermilion-rockfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fvermilion_rockfish%2Fnoaa-mbnms-vermilion-rockfish%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Monterey Bay National Marine Sanctuary',
    sourceName: 'SIMoN / MBNMS via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sebastes_miniatus.jpg',
    photographer: 'Chad King / SIMoN / MBNMS',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Sebastes_miniatus.jpg',
    creditText: 'Chad King / SIMoN / MBNMS / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Vermilion Rockfish reference. Excellent coloration/detail, but the rear body is cropped, so display-only for scanner gating.',
  },
  {
    id: 'commons-macrophyseter-white-sturgeon',
    canonicalSpeciesId: 'white_sturgeon',
    scientificName: 'Acipenser transmontanus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_sturgeon%2Fcommons-macrophyseter-white-sturgeon%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_sturgeon%2Fcommons-macrophyseter-white-sturgeon%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_sturgeon%2Fcommons-macrophyseter-white-sturgeon%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_sturgeon%2Fcommons-macrophyseter-white-sturgeon%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:White_Sturgeon_Monterey_Bay_Aquarium.jpg',
    photographer: 'Macrophyseter',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Macrophyseter / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only aquarium White Sturgeon with an unusually clean full-body side profile. Scanner-eligible; Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-chs-albacore-1910',
    canonicalSpeciesId: 'albacore_tuna',
    scientificName: 'Thunnus alalunga',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falbacore_tuna%2Fcommons-chs-albacore-1910%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falbacore_tuna%2Fcommons-chs-albacore-1910%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falbacore_tuna%2Fcommons-chs-albacore-1910%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falbacore_tuna%2Fcommons-chs-albacore-1910%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'USC Libraries / California Historical Society',
    sourceName: 'USC Digital Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Albacore_tuna_fish_caught_on_Santa_Catalina_Island,_ca.1910_(CHS-373).jpg',
    photographer: 'Unknown / California Historical Society Collection',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Albacore_tuna_fish_caught_on_Santa_Catalina_Island,_ca.1910_(CHS-373).jpg',
    creditText: 'California Historical Society Collection / USC Libraries',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only historical Albacore specimen photograph from circa 1910. Public domain in the United States; black-and-white historical context keeps it display-only.',
  },
  {
    id: 'commons-sfofishing-calico-surfperch',
    canonicalSpeciesId: 'surfperch',
    scientificName: 'Embiotocidae spp.',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurfperch%2Fcommons-sfofishing-calico-surfperch%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurfperch%2Fcommons-sfofishing-calico-surfperch%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurfperch%2Fcommons-sfofishing-calico-surfperch%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurfperch%2Fcommons-sfofishing-calico-surfperch%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Calico_surfperch.jpg',
    photographer: 'SFOfishing',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'SFOfishing / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Calico Surfperch representative image for Roddo’s broad Surfperch / Embiotocidae spp. catalog record. Display-only because one species photo must not be treated as scanner evidence for an entire family/group.',
  },
  {
    id: 'commons-newtonsneurosci-copper-rockfish',
    canonicalSpeciesId: 'copper_rockfish',
    scientificName: 'Sebastes caurinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcopper_rockfish%2Fcommons-newtonsneurosci-copper-rockfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcopper_rockfish%2Fcommons-newtonsneurosci-copper-rockfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcopper_rockfish%2Fcommons-newtonsneurosci-copper-rockfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcopper_rockfish%2Fcommons-newtonsneurosci-copper-rockfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Copper_Rockfish.jpg',
    photographer: 'Newtonsneurosci',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Newtonsneurosci / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Copper Rockfish with clear full-body morphology and natural post-catch coloration. Scanner-eligible; Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'noaa-mbnms-black-rockfish',
    canonicalSpeciesId: 'black_rockfish',
    scientificName: 'Sebastes melanops',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_rockfish%2Fnoaa-mbnms-black-rockfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_rockfish%2Fnoaa-mbnms-black-rockfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_rockfish%2Fnoaa-mbnms-black-rockfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_rockfish%2Fnoaa-mbnms-black-rockfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Monterey Bay National Marine Sanctuary',
    sourceName: 'SIMoN / MBNMS via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sebastes_melanops.jpg',
    photographer: 'Chad King / SIMoN / MBNMS',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Sebastes_melanops.jpg',
    creditText: 'Chad King / SIMoN / MBNMS / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Black Rockfish (Sebastes melanops). Clear primary lateral morphology; small background fish do not obstruct the subject. Public-domain NOAA/SIMoN source.',
  },
  {
    id: 'commons-adelitavrana-canary-rockfish',
    canonicalSpeciesId: 'canary_rockfish',
    scientificName: 'Sebastes pinniger',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcanary_rockfish%2Fcommons-adelitavrana-canary-rockfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcanary_rockfish%2Fcommons-adelitavrana-canary-rockfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcanary_rockfish%2Fcommons-adelitavrana-canary-rockfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcanary_rockfish%2Fcommons-adelitavrana-canary-rockfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Canary_Rockfish.jpg',
    photographer: 'AdelitaVrana',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'AdelitaVrana / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only aquarium Canary Rockfish reference. Strong primary subject but several background fish are visible, so display-only for scanner gating. Roddo WebP derivatives remain CC BY-SA 3.0.',
  },
  {
    id: 'commons-catleeball-california-sheephead',
    canonicalSpeciesId: 'sheepshead_california',
    scientificName: 'Semicossyphus pulcher',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead_california%2Fcommons-catleeball-california-sheephead%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead_california%2Fcommons-catleeball-california-sheephead%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead_california%2Fcommons-catleeball-california-sheephead%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead_california%2Fcommons-catleeball-california-sheephead%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:California_Sheephead_fish_at_Monterey_Bay_Aquarium.jpg',
    photographer: 'Cat Lee Ball',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Cat Lee Ball / Wikimedia Commons · CC0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only aquarium California Sheephead male with a clear, nearly full-body lateral profile and strong diagnostic coloration. CC0; scanner-eligible.',
  },
  {
    id: 'commons-nmoorhatch-pacific-bonito',
    canonicalSpeciesId: 'pacific_bonito',
    scientificName: 'Sarda lineolata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_bonito%2Fcommons-nmoorhatch-pacific-bonito%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_bonito%2Fcommons-nmoorhatch-pacific-bonito%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_bonito%2Fcommons-nmoorhatch-pacific-bonito%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_bonito%2Fcommons-nmoorhatch-pacific-bonito%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons / iNaturalist',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sarda_chiliensis_61671693.jpg',
    photographer: 'nmoorhatch',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'nmoorhatch / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Pacific Bonito catch reference cropped by Roddo to remove the visible human leg/foot from the source frame. Multiple fish and deck context make it display-only. CC BY 4.0 attribution retained.',
  },
  {
    id: 'commons-patrickcox-white-seabass',
    canonicalSpeciesId: 'white_seabass',
    scientificName: 'Atractoscion nobilis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_seabass%2Fcommons-patrickcox-white-seabass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_seabass%2Fcommons-patrickcox-white-seabass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_seabass%2Fcommons-patrickcox-white-seabass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwhite_seabass%2Fcommons-patrickcox-white-seabass%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons / iNaturalist',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atractoscion_nobilis_157006609.jpg',
    photographer: 'Patrick Cox',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Patrick Cox / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only underwater White Seabass school. Useful display reference, but multiple fish prevent scanner eligibility. CC BY 4.0.',
  },
  {
    id: 'noaa-nerr0387-tautog',
    canonicalSpeciesId: 'tautog',
    scientificName: 'Tautoga onitis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftautog%2Fnoaa-nerr0387-tautog%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftautog%2Fnoaa-nerr0387-tautog%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftautog%2Fnoaa-nerr0387-tautog%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftautog%2Fnoaa-nerr0387-tautog%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Tautoga_onitis.jpg',
    photographer: 'NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Tautoga_onitis.jpg',
    creditText: 'NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Tautog reference from NOAA. Two fish are visible and the primary fish is not a clean lateral isolation, so display-only for scanner gating.',
  },
  {
    id: 'smithsonian-serc-12592129064',
    canonicalSpeciesId: 'scup',
    scientificName: 'Stenotomus chrysops',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fscup%2Fsmithsonian-serc-12592129064%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fscup%2Fsmithsonian-serc-12592129064%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fscup%2Fsmithsonian-serc-12592129064%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fscup%2Fsmithsonian-serc-12592129064%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'juvenile',
    sourceAgency: 'Smithsonian Environmental Research Center / Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Stenotomus_chrysops_(S0568)_(12592129064).jpg',
    photographer: 'Robert Aguilar / Smithsonian Environmental Research Center',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Robert Aguilar / Smithsonian Environmental Research Center · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Scup morphology reference. Clean full-body lateral profile; CC BY 2.0; scanner-eligible.',
  },
  {
    id: 'smithsonian-serc-12593682393',
    canonicalSpeciesId: 'summer_flounder',
    scientificName: 'Paralichthys dentatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsummer_flounder%2Fsmithsonian-serc-12593682393%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsummer_flounder%2Fsmithsonian-serc-12593682393%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsummer_flounder%2Fsmithsonian-serc-12593682393%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsummer_flounder%2Fsmithsonian-serc-12593682393%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'juvenile',
    sourceAgency: 'Smithsonian Environmental Research Center / Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Paralichthys_dentatus_(S0225)_(12593682393).jpg',
    photographer: 'Robert Aguilar / Smithsonian Environmental Research Center',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Robert Aguilar / Smithsonian Environmental Research Center · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Summer Flounder reference with diagnostic body shape and ocelli. CC BY 2.0; scanner-eligible.',
  },
  {
    id: 'commons-cliff-northern-puffer',
    canonicalSpeciesId: 'northern_puffer',
    scientificName: 'Sphoeroides maculatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_puffer%2Fcommons-cliff-northern-puffer%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_puffer%2Fcommons-cliff-northern-puffer%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_puffer%2Fcommons-cliff-northern-puffer%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_puffer%2Fcommons-cliff-northern-puffer%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Northern_Puffer_(Sphoeroides_maculatus).jpg',
    photographer: 'Cliff',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Cliff / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only aquarium Northern Puffer with clear full-body profile and diagnostic markings. CC BY 2.0; scanner-eligible.',
  },
  {
    id: 'nasa-ksc-98pc-415',
    canonicalSpeciesId: 'oyster_toadfish',
    scientificName: 'Opsanus tau',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster_toadfish%2Fnasa-ksc-98pc-415%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster_toadfish%2Fnasa-ksc-98pc-415%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster_toadfish%2Fnasa-ksc-98pc-415%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster_toadfish%2Fnasa-ksc-98pc-415%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NASA',
    sourceName: 'NASA / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Opsanus_tau.jpg',
    photographer: 'NASA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Opsanus_tau.jpg',
    creditText: 'NASA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only NASA holding-tank Oyster Toadfish reference. Strong diagnostic morphology, but artificial tank context and angle keep it display-only for scanner gating.',
  },
  {
    id: 'commons-tscharntke-atlantic-mackerel',
    canonicalSpeciesId: 'atlantic_mackerel',
    scientificName: 'Scomber scombrus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fatlantic_mackerel%2Fcommons-tscharntke-atlantic-mackerel%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fatlantic_mackerel%2Fcommons-tscharntke-atlantic-mackerel%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fatlantic_mackerel%2Fcommons-tscharntke-atlantic-mackerel%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fatlantic_mackerel%2Fcommons-tscharntke-atlantic-mackerel%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_mackerel_fish.jpg',
    photographer: 'Titus Tscharntke',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_mackerel_fish.jpg',
    creditText: 'Titus Tscharntke / Wikimedia Commons',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only clean lateral Atlantic Mackerel reference on a neutral background. Released into the public domain by the author; scanner-eligible.',
  },
  {
    id: 'noaa-lagodon-rhomboides',
    canonicalSpeciesId: 'pinfish',
    scientificName: 'Lagodon rhomboides',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpinfish%2Fnoaa-lagodon-rhomboides%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpinfish%2Fnoaa-lagodon-rhomboides%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpinfish%2Fnoaa-lagodon-rhomboides%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpinfish%2Fnoaa-lagodon-rhomboides%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Wikimedia Commons',
    sourceName: 'NOAA Photo Library / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lagodon_rhomboides_NOAA.jpg',
    photographer: 'SEFSC Pascagoula Laboratory; Collection of Brandi Noble, NOAA/NMFS/SEFSC',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Lagodon_rhomboides_NOAA.jpg',
    creditText: 'NOAA/NMFS/SEFSC / Brandi Noble',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated full-body Pinfish reference from NOAA materials. Clear diagnostic side profile; scanner-eligible.',
  },
  {
    id: 'noaa-orthopristis-chrysoptera',
    canonicalSpeciesId: 'pigfish',
    scientificName: 'Orthopristis chrysoptera',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpigfish%2Fnoaa-orthopristis-chrysoptera%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpigfish%2Fnoaa-orthopristis-chrysoptera%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpigfish%2Fnoaa-orthopristis-chrysoptera%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpigfish%2Fnoaa-orthopristis-chrysoptera%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Wikimedia Commons',
    sourceName: 'NOAA Photo Library / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Orthopristis_chrysoptera.jpg',
    photographer: 'SEFSC Pascagoula Laboratory; Collection of Brandi Noble, NOAA/NMFS/SEFSC',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Orthopristis_chrysoptera.jpg',
    creditText: 'NOAA/NMFS/SEFSC / Brandi Noble',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Gulf of Mexico Pigfish reference from NOAA. Clear lateral diagnostic morphology; scanner-eligible.',
  },
  {
    id: 'commons-yinan-chen-hardhead',
    canonicalSpeciesId: 'hardhead_catfish',
    scientificName: 'Ariopsis felis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhardhead_catfish%2Fcommons-yinan-chen-hardhead%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhardhead_catfish%2Fcommons-yinan-chen-hardhead%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhardhead_catfish%2Fcommons-yinan-chen-hardhead%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhardhead_catfish%2Fcommons-yinan-chen-hardhead%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Gfp-hardhead-catfish.jpg',
    photographer: 'Yinan Chen',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Gfp-hardhead-catfish.jpg',
    creditText: 'Yinan Chen / Wikimedia Commons',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free underwater Hardhead Catfish reference. Two fish and frontal framing make it useful for display but not scanner evidence.',
  },
  {
    id: 'plos-wtoller-tripletail',
    canonicalSpeciesId: 'triple_tail',
    scientificName: 'Lobotes surinamensis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftriple_tail%2Fplos-wtoller-tripletail%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftriple_tail%2Fplos-wtoller-tripletail%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftriple_tail%2Fplos-wtoller-tripletail%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftriple_tail%2Fplos-wtoller-tripletail%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'PLOS / Wikimedia Commons',
    sourceName: 'PLOS ONE / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lobotes_surinamensis_-_pone.0010676.g088.png',
    photographer: 'W. Toller',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'W. Toller / PLOS ONE · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Atlantic Tripletail side profile on a clean background. Strong morphology reference; scanner-eligible.',
  },
  {
    id: 'noaa-red-snapper-lcampechanus',
    canonicalSpeciesId: 'red_snapper',
    scientificName: 'Lutjanus campechanus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_snapper%2Fnoaa-red-snapper-lcampechanus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_snapper%2Fnoaa-red-snapper-lcampechanus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_snapper%2Fnoaa-red-snapper-lcampechanus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_snapper%2Fnoaa-red-snapper-lcampechanus%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_campechanus.jpg',
    photographer: 'SEFSC Pascagoula Laboratory; Collection of Brandi Noble, NOAA/NMFS/SEFSC',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_campechanus.jpg',
    creditText: 'NOAA/NMFS/SEFSC · Brandi Noble Collection',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Red Snapper side profile on a clean blue background. Strong morphology reference.',
  },
  {
    id: 'smithsonian-robertson-cobia',
    canonicalSpeciesId: 'cobia',
    scientificName: 'Rachycentron canadum',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcobia%2Fsmithsonian-robertson-cobia%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcobia%2Fsmithsonian-robertson-cobia%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcobia%2Fsmithsonian-robertson-cobia%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcobia%2Fsmithsonian-robertson-cobia%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Smithsonian Institution',
    sourceName: 'Smithsonian / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Rachycentron_canadum_Robertson.jpg',
    photographer: 'D. Ross Robertson',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Rachycentron_canadum_Robertson.jpg',
    creditText: 'D. Ross Robertson / Smithsonian Institution',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Cobia side profile. Smithsonian federal-employee public-domain source; strong morphology reference.',
  },
  {
    id: 'noaa-king-mackerel-cavalla',
    canonicalSpeciesId: 'king_mackerel',
    scientificName: 'Scomberomorus cavalla',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fking_mackerel%2Fnoaa-king-mackerel-cavalla%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fking_mackerel%2Fnoaa-king-mackerel-cavalla%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fking_mackerel%2Fnoaa-king-mackerel-cavalla%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fking_mackerel%2Fnoaa-king-mackerel-cavalla%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:King_mackerel_(_Scomberomorus_cavalla_).jpg',
    photographer: 'NOAA Fisheries Collection; SEFSC Pascagoula Laboratory; Collection of Brandi Noble',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:King_mackerel_(_Scomberomorus_cavalla_).jpg',
    creditText: 'NOAA/NMFS/SEFSC · Brandi Noble Collection',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated King Mackerel side profile on a clean blue background. Strong morphology reference.',
  },
  {
    id: 'serc-lee-weigt-weakfish',
    canonicalSpeciesId: 'weakfish',
    scientificName: 'Cynoscion regalis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fweakfish%2Fserc-lee-weigt-weakfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fweakfish%2Fserc-lee-weigt-weakfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fweakfish%2Fserc-lee-weigt-weakfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fweakfish%2Fserc-lee-weigt-weakfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Smithsonian Environmental Research Center',
    sourceName: 'SERC Photos / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cynoscion_regalis_(S0001)_(12679093363).jpg',
    photographer: 'Lee Weigt / Smithsonian National Museum of Natural History',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Lee Weigt / Smithsonian SERC · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only juvenile Weakfish side profile. Clear diagnostic morphology; CC BY 2.0 attribution preserved.',
  },
  {
    id: 'noaa-ladyfish-elops-saurus',
    canonicalSpeciesId: 'ladyfish',
    scientificName: 'Elops saurus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fladyfish%2Fnoaa-ladyfish-elops-saurus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fladyfish%2Fnoaa-ladyfish-elops-saurus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fladyfish%2Fnoaa-ladyfish-elops-saurus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fladyfish%2Fnoaa-ladyfish-elops-saurus%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Elops_saurus.jpg',
    photographer: 'SEFSC Pascagoula Laboratory; Collection of Brandi Noble, NOAA/NMFS/SEFSC',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Elops_saurus.jpg',
    creditText: 'NOAA/NMFS/SEFSC · Brandi Noble Collection',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only isolated Ladyfish side profile on a clean neutral background. Strong morphology reference.',
  },
  {
    id: 'noaa-fishwatch-mahi',
    canonicalSpeciesId: 'mahi_mahi',
    scientificName: 'Coryphaena hippurus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahi_mahi%2Fnoaa-fishwatch-mahi%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahi_mahi%2Fnoaa-fishwatch-mahi%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahi_mahi%2Fnoaa-fishwatch-mahi%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahi_mahi%2Fnoaa-fishwatch-mahi%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA FishWatch via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Coryphaena_hippurus.png',
    photographer: 'NOAA FishWatch',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Coryphaena_hippurus.png',
    creditText: 'NOAA FishWatch',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only NOAA FishWatch illustration on a transparent background. Strong display morphology; excluded from photo scanner evidence because it is an illustration.',
  },
  {
    id: 'noaa-fishwatch-wahoo',
    canonicalSpeciesId: 'wahoo_sea',
    scientificName: 'Acanthocybium solandri',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwahoo_sea%2Fnoaa-fishwatch-wahoo%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwahoo_sea%2Fnoaa-fishwatch-wahoo%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwahoo_sea%2Fnoaa-fishwatch-wahoo%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwahoo_sea%2Fnoaa-fishwatch-wahoo%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA FishWatch via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Acanthocybium_solandri.png',
    photographer: 'NOAA FishWatch',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Acanthocybium_solandri.png',
    creditText: 'NOAA FishWatch',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only NOAA FishWatch illustration. Excellent display morphology; excluded from photo scanner evidence because it is an illustration.',
  },
  {
    id: 'noaa-reef0735-giant-trevally',
    canonicalSpeciesId: 'giant_trevally_sea',
    scientificName: 'Caranx ignobilis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_trevally_sea%2Fnoaa-reef0735-giant-trevally%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_trevally_sea%2Fnoaa-reef0735-giant-trevally%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_trevally_sea%2Fnoaa-reef0735-giant-trevally%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_trevally_sea%2Fnoaa-reef0735-giant-trevally%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Coral Kingdom Collection via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Caranx_ignobilis.jpg',
    photographer: 'Dr. Dwayne Meadows, NOAA/NMFS/OPR',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Caranx_ignobilis.jpg',
    creditText: 'Dr. Dwayne Meadows / NOAA NMFS OPR',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only underwater Giant Trevally reference from the Northwestern Hawaiian Islands. Clear body shape and head profile; strong scanner reference candidate.',
  },
  {
    id: 'commons-ruff-tuff-bonefish',
    canonicalSpeciesId: 'bonefish',
    scientificName: 'Albula vulpes',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbonefish%2Fcommons-ruff-tuff-bonefish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbonefish%2Fcommons-ruff-tuff-bonefish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbonefish%2Fcommons-ruff-tuff-bonefish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbonefish%2Fcommons-ruff-tuff-bonefish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Albula_vulpes_form.jpg',
    photographer: 'Ruff tuff cream puff',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Ruff tuff cream puff / Wikimedia Commons · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Bonefish specimen reference from San Diego Bay. CC0. Strong display morphology; held out of scanner evidence because of specimen/ruler context.',
  },
  {
    id: 'noaa-fishwatch-skipjack',
    canonicalSpeciesId: 'skipjack_tuna',
    scientificName: 'Katsuwonus pelamis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fskipjack_tuna%2Fnoaa-fishwatch-skipjack%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fskipjack_tuna%2Fnoaa-fishwatch-skipjack%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fskipjack_tuna%2Fnoaa-fishwatch-skipjack%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fskipjack_tuna%2Fnoaa-fishwatch-skipjack%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA FishWatch via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Katsuwonus_pelamis.png',
    photographer: 'NOAA FishWatch / unknown author',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Katsuwonus_pelamis.png',
    creditText: 'NOAA FishWatch',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only NOAA FishWatch illustration of Skipjack Tuna. Strong display morphology; excluded from photo scanner evidence because it is an illustration.',
  },
  {
    id: 'noaa-atlantic-blue-marlin',
    canonicalSpeciesId: 'blue_marlin',
    scientificName: 'Makaira nigricans',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_marlin%2Fnoaa-atlantic-blue-marlin%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_marlin%2Fnoaa-atlantic-blue-marlin%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_marlin%2Fnoaa-atlantic-blue-marlin%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_marlin%2Fnoaa-atlantic-blue-marlin%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_blue_marlin.jpg',
    photographer: 'NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_blue_marlin.jpg',
    creditText: 'NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Atlantic Blue Marlin field image. No people visible. Display/reference useful; held out of scanner evidence because the fish is breaching at an oblique angle and partially cropped by the source frame.',
  },
  {
    id: 'noaa-great-barracuda-pascagoula',
    canonicalSpeciesId: 'great_barracuda',
    scientificName: 'Sphyraena barracuda',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgreat_barracuda%2Fnoaa-great-barracuda-pascagoula%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgreat_barracuda%2Fnoaa-great-barracuda-pascagoula%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgreat_barracuda%2Fnoaa-great-barracuda-pascagoula%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgreat_barracuda%2Fnoaa-great-barracuda-pascagoula%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sphyraena_barracuda_by_NOAA.jpg',
    photographer: 'SEFSC Pascagoula Laboratory; Brandi Noble collection',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Sphyraena_barracuda_by_NOAA.jpg',
    creditText: 'NOAA/NMFS/SEFSC Pascagoula Laboratory',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only Great Barracuda specimen reference. Clear lateral morphology but ruler/deck context makes it display-only for scanner gating.',
  },
  {
    id: 'noaa-onaga-shimada',
    canonicalSpeciesId: 'onaga',
    scientificName: 'Etelis coruscans',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper_sea%2Fnoaa-onaga-shimada%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper_sea%2Fnoaa-onaga-shimada%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper_sea%2Fnoaa-onaga-shimada%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper_sea%2Fnoaa-onaga-shimada%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / NMFS',
    sourceName: 'NOAA Fisheries Collection via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Fis01167_(28136581836).jpg',
    photographer: 'Allen Shimada, NOAA/NMFS/OST',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Allen Shimada / NOAA NMFS OST · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Roddo derivative is cropped to the top Long-tail Red Snapper (Onaga) only, removing the second species from the source frame. Human-free. CC BY 2.0 derivative; display reference only because this is one representative snapper species rather than a generic scanner exemplar.',
  },
  {
    id: 'noaa-reef0949-rabbitfish',
    canonicalSpeciesId: 'rabbitfish',
    scientificName: 'Siganus spinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frabbitfish%2Fnoaa-reef0949-rabbitfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frabbitfish%2Fnoaa-reef0949-rabbitfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frabbitfish%2Fnoaa-reef0949-rabbitfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frabbitfish%2Fnoaa-reef0949-rabbitfish%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA Photo Library',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Reef0949_-_Flickr_-_NOAA_Photo_Library.jpg',
    photographer: 'David Burdick',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'David Burdick / NOAA Photo Library · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural reef group of Scribbled Rabbitfish. No people visible. CC BY 2.0; useful display habitat reference but multiple fish make it unsuitable for scanner evidence.',
  },
  {
    id: 'commons-gymnosarda-29564501',
    canonicalSpeciesId: 'dogtooth_tuna',
    scientificName: 'Gymnosarda unicolor',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdogtooth_tuna%2Fcommons-gymnosarda-29564501%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdogtooth_tuna%2Fcommons-gymnosarda-29564501%2Fthumb.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Gymnosarda_unicolor_29564501.jpg',
    photographer: 'Pranav Vissanji',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Pranav Vissanji / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Dogtooth Tuna reference. Clear full-body profile; no people visible; scanner-eligible.',
  },
  {
    id: 'usfda-chanos-rfeimg-0333',
    canonicalSpeciesId: 'milkfish',
    scientificName: 'Chanos chanos',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmilkfish%2Fusfda-chanos-rfeimg-0333%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmilkfish%2Fusfda-chanos-rfeimg-0333%2Fthumb.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Food and Drug Administration',
    sourceName: 'FDA Regulatory Fish Encyclopedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Chanos_chanos_(RFEIMG-0333).jpg',
    photographer: 'Brad Tenge / U.S. FDA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Chanos_chanos_(RFEIMG-0333).jpg',
    creditText: 'Brad Tenge / U.S. Food and Drug Administration',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free FDA specimen/reference image. Excellent morphology display reference; scanner held out because ruler/color-control specimen context is present.',
  },
  {
    id: 'commons-lutjanus-argentimaculatus',
    canonicalSpeciesId: 'mangrove_jack',
    scientificName: 'Lutjanus argentimaculatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_jack%2Fcommons-lutjanus-argentimaculatus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmangrove_jack%2Fcommons-lutjanus-argentimaculatus%2Fthumb.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_argentimaculatus.jpg',
    photographer: 'Christophe Cadet',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Christophe Cadet / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Mangrove Jack reference. Single unobstructed fish; no people visible; scanner-eligible.',
  },
  {
    id: 'commons-epinephelus-coioides-129018144',
    canonicalSpeciesId: 'grouper',
    scientificName: 'Epinephelus coioides',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrouper%2Fcommons-epinephelus-coioides-129018144%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrouper%2Fcommons-epinephelus-coioides-129018144%2Fthumb.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Epinephelus_coioides_129018144.jpg',
    photographer: 'Bruce Deagle',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Bruce Deagle / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only natural underwater Orange-spotted Grouper reference attached to Roddo generic Grouper profile. Single clear fish; no people visible; scanner-eligible as representative Epinephelus morphology.',
  },
  {
    id: 'fishbase-scomberoides-commersonnianus',
    canonicalSpeciesId: 'queenfish',
    scientificName: 'Scomberoides commersonnianus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fqueenfish%2Ffishbase-scomberoides-commersonnianus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fqueenfish%2Ffishbase-scomberoides-commersonnianus%2Fthumb.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'FishBase / Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Scomberoides_commersonnianus.jpg',
    photographer: 'Abdullajid U. Basali',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/3.0/',
    creditText: 'Abdullajid U. Basali / FishBase · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Queenfish reference. Multiple fish are visible, so it is display-only and excluded from scanner evidence.',
  },

  {
    id: 'commons-hampala-ratmuangkhwang',
    canonicalSpeciesId: 'hampala_barb',
    scientificName: 'Hampala macrolepidota',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhampala_barb%2Fcommons-hampala-ratmuangkhwang%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhampala_barb%2Fcommons-hampala-ratmuangkhwang%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhampala_barb%2Fcommons-hampala-ratmuangkhwang%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhampala_barb%2Fcommons-hampala-ratmuangkhwang%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons / FishBase',
    sourceName: 'Hampala macrolepidota',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Hampala_macrolepidota.jpg',
    photographer: 'Sahat Ratmuangkhwang',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/3.0/',
    creditText: 'Sahat Ratmuangkhwang / FishBase / Wikimedia Commons · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only lateral reference. Exact Hampala macrolepidota match. Roddo WebP derivatives are resized/re-encoded and attribution is retained.',
  },
  {
    id: 'commons-golden-mahseer-sharp-2019',
    canonicalSpeciesId: 'mahseer',
    scientificName: 'Tor spp.',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahseer%2Fcommons-golden-mahseer-sharp-2019%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahseer%2Fcommons-golden-mahseer-sharp-2019%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahseer%2Fcommons-golden-mahseer-sharp-2019%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmahseer%2Fcommons-golden-mahseer-sharp-2019%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Golden mahseer (Tor putitora) Babai River',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Golden_mahseer_(Tor_putitora)_Babai_River.jpg',
    photographer: 'Charles J. Sharp',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Charles J. Sharp / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only wild shoal of Tor putitora. Roddo catalog is Tor spp., so this is a representative Mahseer display reference rather than scanner evidence. Derivatives remain under CC BY-SA 4.0.',
  },
  {
    id: 'commons-eleutheronema-bedo-2015',
    canonicalSpeciesId: 'threadfin',
    scientificName: 'Polynemidae spp.',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fthreadfin%2Fcommons-eleutheronema-bedo-2015%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fthreadfin%2Fcommons-eleutheronema-bedo-2015%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fthreadfin%2Fcommons-eleutheronema-bedo-2015%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fthreadfin%2Fcommons-eleutheronema-bedo-2015%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Eleutheronema tetradactylum (Shaw, 1804)',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Eleutheronema_tetradactylum(Shaw,_1804).jpg',
    photographer: 'BEDO (Thailand)',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'BEDO (Thailand) / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only lateral image of Eleutheronema tetradactylum. Roddo Threadfin entry is family-level Polynemidae spp.; representative display reference only. Derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-spanish-mackerel-1884',
    canonicalSpeciesId: 'spanish_mackerel',
    scientificName: 'Scomberomorus maculatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspanish_mackerel%2Fcommons-spanish-mackerel-1884%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspanish_mackerel%2Fcommons-spanish-mackerel-1884%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspanish_mackerel%2Fcommons-spanish-mackerel-1884%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspanish_mackerel%2Fcommons-spanish-mackerel-1884%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'The fishes of the east Atlantic coast',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Scomberomorus_maculatus.jpg',
    photographer: 'Unknown author (1884)',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Scomberomorus_maculatus.jpg',
    creditText: 'The fishes of the east Atlantic coast (1884) / Wikimedia Commons · Public domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain historical illustration of the exact Roddo Spanish Mackerel species Scomberomorus maculatus. Display reference only; excluded from photo scanner evidence.',
  },
  {
    id: 'commons-channa-micropeltes-chrumps',
    canonicalSpeciesId: 'giant_snakehead',
    scientificName: 'Channa micropeltes',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_snakehead%2Fcommons-channa-micropeltes-chrumps%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_snakehead%2Fcommons-channa-micropeltes-chrumps%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_snakehead%2Fcommons-channa-micropeltes-chrumps%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_snakehead%2Fcommons-channa-micropeltes-chrumps%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Channa micropeltes',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Channa_micropeltes.jpg',
    photographer: 'Chrumps',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Chrumps / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Fish-only aquarium image of Channa micropeltes. Useful display reference; partial-body framing keeps it out of scanner evidence. Derivatives remain CC BY-SA 3.0.',
  },

  {
    id: 'fws-alligator-gar-61',
    canonicalSpeciesId: 'alligator_gar',
    scientificName: 'Atractosteus spatula',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falligator_gar%2Ffws-alligator-gar-61%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falligator_gar%2Ffws-alligator-gar-61%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falligator_gar%2Ffws-alligator-gar-61%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falligator_gar%2Ffws-alligator-gar-61%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/alligator-gar-61',
    photographer: 'USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/alligator-gar-61',
    creditText: 'USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free close-up Alligator Gar head reference with strong diagnostic snout, teeth, eye, and ganoid scale detail. Public-domain FWS source. Display reference only because the source does not show the full body.',
  },
  {
    id: 'commons-tiia-monto-roach',
    canonicalSpeciesId: 'roach',
    scientificName: 'Rutilus rutilus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Froach%2Fcommons-tiia-monto-roach%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Froach%2Fcommons-tiia-monto-roach%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Froach%2Fcommons-tiia-monto-roach%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Froach%2Fcommons-tiia-monto-roach%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Rutilus rutilus in aquarium',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Rutilus_rutilus_in_aquarium.jpg',
    photographer: 'Tiia Monto',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Tiia Monto / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium school of Common Roach. Strong display reference, but multiple fish are present, so it is excluded from scanner evidence. Roddo WebP derivatives remain CC BY-SA 3.0.',
  },
  {
    id: 'commons-dezidor-tench',
    canonicalSpeciesId: 'tench',
    scientificName: 'Tinca tinca',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftench%2Fcommons-dezidor-tench%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftench%2Fcommons-dezidor-tench%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftench%2Fcommons-dezidor-tench%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftench%2Fcommons-dezidor-tench%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Tinca tinca',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Tinca_tinca.jpeg',
    photographer: 'Dezidor',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/3.0/',
    creditText: 'Dezidor / Wikimedia Commons · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free full-body Tench field reference with clear coloration and morphology. Display/reference use only for now because it is photographed out of water on grass. Roddo WebP derivatives retain CC BY 3.0 attribution.',
  },
  {
    id: 'commons-basilicofresco-zander',
    canonicalSpeciesId: 'zander',
    scientificName: 'Sander lucioperca',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzander%2Fcommons-basilicofresco-zander%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzander%2Fcommons-basilicofresco-zander%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzander%2Fcommons-basilicofresco-zander%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzander%2Fcommons-basilicofresco-zander%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Sander lucioperca, Acquario del Po',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sander_lucioperca,_Acquario_del_Po.jpg',
    photographer: 'Basilicofresco',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Basilicofresco / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Zander with a clean full-body lateral profile and diagnostic vertical barring. Scanner-eligible. Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-ellicrum-common-bream',
    canonicalSpeciesId: 'common_bream',
    scientificName: 'Abramis brama',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_bream%2Fcommons-ellicrum-common-bream%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_bream%2Fcommons-ellicrum-common-bream%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_bream%2Fcommons-ellicrum-common-bream%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcommon_bream%2Fcommons-ellicrum-common-bream%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Abramis brama',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Abramis_brama.png',
    photographer: 'Original: Lvova; derivative: Ellicrum',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Lvova / Ellicrum / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Common Bream cutout on transparent background. Excellent display morphology; held out of scanner evidence because this is a derived cutout rather than a natural-scene reference. Roddo derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-usgs-rudd',
    canonicalSpeciesId: 'rudd',
    scientificName: 'Scardinius erythrophthalmus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frudd%2Fcommons-usgs-rudd%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frudd%2Fcommons-usgs-rudd%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frudd%2Fcommons-usgs-rudd%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frudd%2Fcommons-usgs-rudd%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'USGS / Wikimedia Commons',
    sourceName: 'Scardinius erythrophthalmus',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Scardinius_erythrophthalmus.jpg',
    photographer: 'Noel Burkhead',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/2.5/',
    creditText: 'Noel Burkhead / USGS / Wikimedia Commons · CC BY-SA 2.5',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Rudd reference on a clean blue background. Clear full-body lateral morphology with red fins visible; scanner-eligible. Commons also documents the underlying USGS public-information provenance.',
  },
  {
    id: 'commons-ellicrum-barbel',
    canonicalSpeciesId: 'barbel',
    scientificName: 'Barbus barbus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarbel%2Fcommons-ellicrum-barbel%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarbel%2Fcommons-ellicrum-barbel%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarbel%2Fcommons-ellicrum-barbel%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarbel%2Fcommons-ellicrum-barbel%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Barbus barbus on transparent background',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Barbus_barbus.png',
    photographer: 'Original: Linie29; derivative: Ellicrum',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Linie29 / Ellicrum / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Common Barbel cutout with strong full-body morphology and visible barbels. Display/reference only because this is a derived transparent-background cutout. Roddo derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-af-lydon-chub',
    canonicalSpeciesId: 'chub',
    scientificName: 'Squalius cephalus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchub%2Fcommons-af-lydon-chub%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchub%2Fcommons-af-lydon-chub%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchub%2Fcommons-af-lydon-chub%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fchub%2Fcommons-af-lydon-chub%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons / Biodiversity Heritage Library',
    sourceName: 'British Fresh-water Fishes (1879)',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Squalius_cephalus.jpg',
    photographer: 'A. F. Lydon',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/mark/1.0/',
    creditText: 'A. F. Lydon / British Fresh-water Fishes (1879) · Public domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free historical Chub illustration with clear full-body morphology. Public-domain display reference; excluded from photo scanner evidence because it is an illustration.',
  },
  {
    id: 'commons-ellicrum-dace',
    canonicalSpeciesId: 'dace',
    scientificName: 'Leuciscus leuciscus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdace%2Fcommons-ellicrum-dace%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdace%2Fcommons-ellicrum-dace%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdace%2Fcommons-ellicrum-dace%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdace%2Fcommons-ellicrum-dace%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Leuciscus leuciscus on transparent background',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Leuciscus_leuciscus.png',
    photographer: 'Original: Bloopityboop; derivative: Ellicrum',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Bloopityboop / Ellicrum / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Common Dace cutout with clear full-body morphology. Display/reference only because this is a derived transparent-background cutout. Roddo derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-taunagi-red-seabream',
    canonicalSpeciesId: 'red_seabream',
    scientificName: 'Pagrus major',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_seabream%2Fcommons-taunagi-red-seabream%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_seabream%2Fcommons-taunagi-red-seabream%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_seabream%2Fcommons-taunagi-red-seabream%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_seabream%2Fcommons-taunagi-red-seabream%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Pagrus major in ioworld Kagoshima aquarium',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Pagrus_major_ioworld.jpg',
    photographer: 'タウナギ',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'タウナギ / Wikimedia Commons · CC0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Red Seabream reference. Strong head/body detail, but the tail is cropped and background fish are visible, so display-only for scanner gating. CC0.',
  },
  {
    id: 'commons-totti-lateolabrax-iwaki',
    canonicalSpeciesId: 'japanese_seabass',
    scientificName: 'Lateolabrax japonicus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_seabass%2Fcommons-totti-lateolabrax-iwaki%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_seabass%2Fcommons-totti-lateolabrax-iwaki%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_seabass%2Fcommons-totti-lateolabrax-iwaki%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_seabass%2Fcommons-totti-lateolabrax-iwaki%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lateolabrax_japonicus_Iwaki.jpg',
    photographer: 'Totti',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Totti / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Japanese Seabass reference with a strong full-body lateral profile. Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-tmdsa-black-porgy',
    canonicalSpeciesId: 'black_porgy',
    scientificName: 'Acanthopagrus schlegelii',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_porgy%2Fcommons-tmdsa-black-porgy%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_porgy%2Fcommons-tmdsa-black-porgy%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_porgy%2Fcommons-tmdsa-black-porgy%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_porgy%2Fcommons-tmdsa-black-porgy%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Acanthopagrus_schlegelii.JPG',
    photographer: 'TMDSA',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'TMDSA / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Black Porgy catch reference with clear diagnostic body form. Hook/net context keeps it display-only for scanner evidence.',
  },
  {
    id: 'commons-daiju-azuma-paralichthys',
    canonicalSpeciesId: 'japanese_flounder',
    scientificName: 'Paralichthys olivaceus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_flounder%2Fcommons-daiju-azuma-paralichthys%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_flounder%2Fcommons-daiju-azuma-paralichthys%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_flounder%2Fcommons-daiju-azuma-paralichthys%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjapanese_flounder%2Fcommons-daiju-azuma-paralichthys%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Paralichthys_olivaceus.jpg',
    photographer: 'Daiju Azuma',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/2.5/',
    creditText: 'Daiju Azuma / Wikimedia Commons · CC BY-SA 2.5',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Japanese Flounder reference showing full flatfish morphology. Aquarium/top-angle context keeps it display-only for scanner evidence.',
  },
  {
    id: 'commons-totti-hairtail-umitamag',
    canonicalSpeciesId: 'hairtail',
    scientificName: 'Trichiurus lepturus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhairtail%2Fcommons-totti-hairtail-umitamag%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhairtail%2Fcommons-totti-hairtail-umitamag%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhairtail%2Fcommons-totti-hairtail-umitamag%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhairtail%2Fcommons-totti-hairtail-umitamag%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Trichiurus_lepturus_Umitamago.jpg',
    photographer: 'Totti',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Totti / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Largehead Hairtail reference. A strong primary fish is visible, but multiple fish are present, so display-only for scanner gating.',
  },
  {
    id: 'commons-jaredmckenzie-yellowfin-bream',
    canonicalSpeciesId: 'yellowfin_bream',
    scientificName: 'Acanthopagrus australis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_bream%2Fcommons-jaredmckenzie-yellowfin-bream%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_bream%2Fcommons-jaredmckenzie-yellowfin-bream%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_bream%2Fcommons-jaredmckenzie-yellowfin-bream%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_bream%2Fcommons-jaredmckenzie-yellowfin-bream%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Acanthopagrus_australis_on_measuring_mat.jpg',
    photographer: 'JaredMcKenzie',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'JaredMcKenzie / Wikimedia Commons · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Yellowfin Bream catch/reference image with clear full-body morphology. Measuring-mat context keeps it display-only for scanner evidence.',
  },
  {
    id: 'commons-richard-ling-dusky-flathead',
    canonicalSpeciesId: 'dusky_flathead',
    scientificName: 'Platycephalus fuscus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdusky_flathead%2Fcommons-richard-ling-dusky-flathead%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdusky_flathead%2Fcommons-richard-ling-dusky-flathead%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdusky_flathead%2Fcommons-richard-ling-dusky-flathead%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdusky_flathead%2Fcommons-richard-ling-dusky-flathead%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Platycephalus_fuscus.jpg',
    photographer: 'Richard Ling',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/2.0/',
    creditText: 'Richard Ling / Wikimedia Commons · CC BY-SA 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural underwater Dusky Flathead reference. Full diagnostic body shape and camouflage are visible; Roddo WebP derivatives remain CC BY-SA 2.0.',
  },
  {
    id: 'commons-show-ryu-argyrosomus',
    canonicalSpeciesId: 'mulloway',
    scientificName: 'Argyrosomus japonicus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmulloway%2Fcommons-show-ryu-argyrosomus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmulloway%2Fcommons-show-ryu-argyrosomus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmulloway%2Fcommons-show-ryu-argyrosomus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmulloway%2Fcommons-show-ryu-argyrosomus%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Argyrosomus_japonicus.JPG',
    photographer: 'Show_ryu',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Show_ryu / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Mulloway/Jewfish reference with a clean lateral profile. Roddo WebP derivatives use the CC BY-SA 3.0 option offered on the source page.',
  },
  {
    id: 'commons-pengo-australian-snapper',
    canonicalSpeciesId: 'australian_snapper',
    scientificName: 'Chrysophrys auratus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Faustralian_snapper%2Fcommons-pengo-australian-snapper%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Faustralian_snapper%2Fcommons-pengo-australian-snapper%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Faustralian_snapper%2Fcommons-pengo-australian-snapper%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Faustralian_snapper%2Fcommons-pengo-australian-snapper%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Chrysophrys_auratus_Pengo.jpg',
    photographer: 'Peter Halasz (Pengo)',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Peter Halasz (Pengo) / Wikimedia Commons · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Melbourne Aquarium Australasian Snapper reference. Strong foreground morphology, but another fish is visible in the background, so display-only for scanner gating.',
  },
  {
    id: 'fishbase-daryl-mcphee-sand-whiting',
    canonicalSpeciesId: 'sand_whiting',
    scientificName: 'Sillago ciliata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsand_whiting%2Ffishbase-daryl-mcphee-sand-whiting%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsand_whiting%2Ffishbase-daryl-mcphee-sand-whiting%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsand_whiting%2Ffishbase-daryl-mcphee-sand-whiting%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsand_whiting%2Ffishbase-daryl-mcphee-sand-whiting%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'FishBase / Wikimedia Commons',
    sourceName: 'FishBase via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sillago_ciliata_Australia.jpg',
    photographer: 'Daryl McPhee',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/3.0/',
    creditText: 'Daryl McPhee / FishBase / Wikimedia Commons · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Roddo derivative crops the lower frame to remove visible human clothing, leaving the Sand Whiting and measuring reference. Display-only because of specimen/measuring context.',
  },
  {
    id: 'commons-mitch-ames-barramundi',
    canonicalSpeciesId: 'barramundi',
    scientificName: 'Lates calcarifer',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarramundi%2Fcommons-mitch-ames-barramundi%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarramundi%2Fcommons-mitch-ames-barramundi%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarramundi%2Fcommons-mitch-ames-barramundi%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarramundi%2Fcommons-mitch-ames-barramundi%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lates_calcarifer,_2014-09-19.JPG',
    photographer: 'Mitch Ames',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Mitch Ames / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Barramundi reference from Canberra with a clear full-body lateral profile. Roddo WebP derivatives remain CC BY-SA 4.0.',
  },
  {
    id: 'commons-petruss-cichla-ocellaris',
    canonicalSpeciesId: 'peacock_bass',
    scientificName: 'Cichla ocellaris',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpeacock_bass%2Fcommons-petruss-cichla-ocellaris%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpeacock_bass%2Fcommons-petruss-cichla-ocellaris%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpeacock_bass%2Fcommons-petruss-cichla-ocellaris%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpeacock_bass%2Fcommons-petruss-cichla-ocellaris%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cichla_ocellaris.jpg',
    photographer: 'Petruss',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Petruss / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Peacock Bass reference. Two fish are visible, so this remains display-only for scanner evidence.',
  },
  {
    id: 'noaa-reef1524-caranx-hippos',
    canonicalSpeciesId: 'jack_crevalle',
    scientificName: 'Caranx hippos',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjack_crevalle%2Fnoaa-reef1524-caranx-hippos%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjack_crevalle%2Fnoaa-reef1524-caranx-hippos%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjack_crevalle%2Fnoaa-reef1524-caranx-hippos%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjack_crevalle%2Fnoaa-reef1524-caranx-hippos%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA Coral Kingdom Collection via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Caranx_hippos.jpg',
    photographer: 'Dr. Anthony R. Picciolo / NOAA NODC',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Caranx_hippos.jpg',
    creditText: 'Dr. Anthony R. Picciolo / NOAA NODC',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural underwater Jack Crevalle school. Strong display reference; multiple fish make it unsuitable for scanner evidence.',
  },
  {
    id: 'smithsonian-serc-12591792133',
    canonicalSpeciesId: 'pompano',
    scientificName: 'Trachinotus carolinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpompano%2Fsmithsonian-serc-12591792133%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpompano%2Fsmithsonian-serc-12591792133%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpompano%2Fsmithsonian-serc-12591792133%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpompano%2Fsmithsonian-serc-12591792133%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Smithsonian Environmental Research Center',
    sourceName: 'SERC Photos via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Trachinotus_carolinus_(S0970)_(12591792133).jpg',
    photographer: 'Robert Aguilar / Smithsonian Environmental Research Center',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Robert Aguilar / Smithsonian Environmental Research Center · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Florida Pompano morphology reference. Clean full-body lateral view; scanner-eligible.',
  },
  {
    id: 'commons-robertson-guadalupe-bass',
    canonicalSpeciesId: 'guadalupe_bass',
    scientificName: 'Micropterus treculii',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fguadalupe_bass%2Fcommons-robertson-guadalupe-bass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fguadalupe_bass%2Fcommons-robertson-guadalupe-bass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fguadalupe_bass%2Fcommons-robertson-guadalupe-bass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fguadalupe_bass%2Fcommons-robertson-guadalupe-bass%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Guadalupe_bass_-_Micropterus_treculii.jpg',
    photographer: 'Clinton & Charles Robertson',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Clinton & Charles Robertson / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free close-up Guadalupe Bass reference. Excellent head/eye detail but not a full-body morphology image, so display-only.',
  },
  {
    id: 'ifremer-00762-87437-seriola-lalandi',
    canonicalSpeciesId: 'yellowtail_amberjack',
    scientificName: 'Seriola lalandi',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack%2Fifremer-00762-87437-seriola-lalandi%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack%2Fifremer-00762-87437-seriola-lalandi%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack%2Fifremer-00762-87437-seriola-lalandi%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowtail_amberjack%2Fifremer-00762-87437-seriola-lalandi%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'IFREMER',
    sourceName: 'IFREMER via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Seriole_(Seriola_lalandi)_(Ifremer_00762-87437).jpg',
    photographer: 'Hugues Evano / IFREMER',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Hugues Evano / IFREMER · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Seriola lalandi specimen/reference image on a white background. Strong display morphology; held out of scanner evidence because it is a laboratory/field-sampling reference.',
  },
  {
    id: 'commons-sannidhishetty-rohu',
    canonicalSpeciesId: 'rohu_sea',
    scientificName: 'Labeo rohita',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frohu_sea%2Fcommons-sannidhishetty-rohu%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frohu_sea%2Fcommons-sannidhishetty-rohu%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frohu_sea%2Fcommons-sannidhishetty-rohu%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frohu_sea%2Fcommons-sannidhishetty-rohu%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Rohu_(_Labeo_rohita_).jpg',
    photographer: 'Sannidhishetty',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Sannidhishetty / Wikimedia Commons · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Rohu reference. Two full fish plus a partial adjacent fish are visible, so this is display-only and excluded from scanner evidence.',
  },
  {
    id: 'commons-mintern-cirrhinus-mrigala',
    canonicalSpeciesId: 'mrigal_carp',
    scientificName: 'Cirrhinus mrigala',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmrigal_carp%2Fcommons-mintern-cirrhinus-mrigala%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmrigal_carp%2Fcommons-mintern-cirrhinus-mrigala%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmrigal_carp%2Fcommons-mintern-cirrhinus-mrigala%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmrigal_carp%2Fcommons-mintern-cirrhinus-mrigala%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Fishes of India (1878) via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cirrhina_mrigala_Mintern_129.jpg',
    photographer: 'R. Mintern',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/mark/1.0/',
    creditText: 'R. Mintern / Fishes of India (1878) · Public Domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free historical morphology plate for Cirrhinus mrigala. Source page notes the original plate identity needs verification, so this remains display-only and excluded from scanner evidence.',
  },
  {
    id: 'usfda-usnm-445335-longtail-tuna',
    canonicalSpeciesId: 'longtail_tuna',
    scientificName: 'Thunnus tonggol',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flongtail_tuna%2Fusfda-usnm-445335-longtail-tuna%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flongtail_tuna%2Fusfda-usnm-445335-longtail-tuna%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flongtail_tuna%2Fusfda-usnm-445335-longtail-tuna%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flongtail_tuna%2Fusfda-usnm-445335-longtail-tuna%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Food and Drug Administration / Smithsonian NMNH',
    sourceName: 'FDA RSSL / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Thunnus_tonggol_(USNM-445335).jpg',
    photographer: 'Jeffrey Williams / Smithsonian NMNH',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Thunnus_tonggol_(USNM-445335).jpg',
    creditText: 'Jeffrey Williams / Smithsonian NMNH · U.S. FDA public domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free voucher specimen reference from the Philippine marine fish DNA barcode library / FDA RSSL. Excellent morphology but specimen/reference context, so display-only for scanner gating.',
  },
  {
    id: 'commons-zerpe-bluefin-trevally',
    canonicalSpeciesId: 'bluefin_trevally',
    scientificName: 'Caranx melampygus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefin_trevally%2Fcommons-zerpe-bluefin-trevally%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefin_trevally%2Fcommons-zerpe-bluefin-trevally%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefin_trevally%2Fcommons-zerpe-bluefin-trevally%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbluefin_trevally%2Fcommons-zerpe-bluefin-trevally%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Bluefin_trevally_(Caranx_melampygus)_(46991298234).jpg',
    photographer: 'Rickard Zerpe',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Rickard Zerpe / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural underwater Bluefin Trevally reference from Raja Ampat. Main fish is clear, but partial/background fish remain in frame, so display-only for scanner evidence.',
  },
  {
    id: 'commons-richard-ling-coral-trout',
    canonicalSpeciesId: 'coral_trout',
    scientificName: 'Plectropomus leopardus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoral_trout%2Fcommons-richard-ling-coral-trout%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoral_trout%2Fcommons-richard-ling-coral-trout%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoral_trout%2Fcommons-richard-ling-coral-trout%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcoral_trout%2Fcommons-richard-ling-coral-trout%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Plectropomus_leopardus_1.jpg',
    photographer: 'Richard Ling',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/2.0/',
    creditText: 'Richard Ling / Wikimedia Commons · CC BY-SA 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural underwater Coral Trout reference from the Great Barrier Reef. Roddo derivatives crop the lower-left source watermark while preserving the full fish; clean lateral morphology is scanner-eligible.',
  },
  {
    id: 'commons-bernard-dupont-channa-striata',
    canonicalSpeciesId: 'striped_snakehead',
    scientificName: 'Channa striata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_snakehead%2Fcommons-bernard-dupont-channa-striata%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_snakehead%2Fcommons-bernard-dupont-channa-striata%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_snakehead%2Fcommons-bernard-dupont-channa-striata%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_snakehead%2Fcommons-bernard-dupont-channa-striata%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Striped_Snakehead_(Channa_striata)_(8684185428).jpg',
    photographer: 'Bernard DUPONT',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/2.0/',
    creditText: 'Bernard DUPONT / Wikimedia Commons · CC BY-SA 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Striped Snakehead reference. Strong primary fish but other fish are visible, so display-only for scanner gating.',
  },
  {
    id: 'commons-cool-critter-mekong-catfish-side',
    canonicalSpeciesId: 'mekong_giant_catfish',
    scientificName: 'Pangasianodon gigas',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmekong_giant_catfish%2Fcommons-cool-critter-mekong-catfish-side%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmekong_giant_catfish%2Fcommons-cool-critter-mekong-catfish-side%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmekong_giant_catfish%2Fcommons-cool-critter-mekong-catfish-side%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmekong_giant_catfish%2Fcommons-cool-critter-mekong-catfish-side%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Mekong_giant_catfish_side.jpg',
    photographer: 'Cool critter channel',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Cool critter channel / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Mekong Giant Catfish reference. Large foreground fish is diagnostic, but multiple fish and oblique framing make it display-only.',
  },
  {
    id: 'commons-nasserhalaweh-giant-siamese-carp',
    canonicalSpeciesId: 'giant_siamese_carp',
    scientificName: 'Catlocarpio siamensis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_siamese_carp%2Fcommons-nasserhalaweh-giant-siamese-carp%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_siamese_carp%2Fcommons-nasserhalaweh-giant-siamese-carp%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_siamese_carp%2Fcommons-nasserhalaweh-giant-siamese-carp%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_siamese_carp%2Fcommons-nasserhalaweh-giant-siamese-carp%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cyprinidae_Catlocarpio_siamensis_2.jpg',
    photographer: 'NasserHalaweh',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'NasserHalaweh / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Giant Siamese Carp reference with a clear nearly full-body lateral profile. Scanner-eligible.',
  },
  {
    id: 'usgs-pam-fuller-clarias-batrachus',
    canonicalSpeciesId: 'walking_catfish',
    scientificName: 'Clarias batrachus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalking_catfish%2Fusgs-pam-fuller-clarias-batrachus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalking_catfish%2Fusgs-pam-fuller-clarias-batrachus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalking_catfish%2Fusgs-pam-fuller-clarias-batrachus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwalking_catfish%2Fusgs-pam-fuller-clarias-batrachus%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Geological Survey',
    sourceName: 'USGS NAS via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Clarias_batrachus.jpg',
    photographer: 'Pam Fuller / USGS',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Clarias_batrachus.jpg',
    creditText: 'Pam Fuller / USGS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free public-domain USGS Walking Catfish reference. Small low-resolution holding-container image; display-only for scanner evidence.',
  },
  {
    id: 'fishbase-sahat-ratmuangkhwang-nile-tilapia',
    canonicalSpeciesId: 'nile_tilapia',
    scientificName: 'Oreochromis niloticus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnile_tilapia%2Ffishbase-sahat-ratmuangkhwang-nile-tilapia%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnile_tilapia%2Ffishbase-sahat-ratmuangkhwang-nile-tilapia%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnile_tilapia%2Ffishbase-sahat-ratmuangkhwang-nile-tilapia%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnile_tilapia%2Ffishbase-sahat-ratmuangkhwang-nile-tilapia%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'FishBase / Wikimedia Commons',
    sourceName: 'FishBase via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Oreochromis_niloticus_Thailand.jpg',
    photographer: 'Sahat Ratmuangkhwang',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/3.0/',
    creditText: 'Sahat Ratmuangkhwang / FishBase · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Nile Tilapia reference from southern Thailand with an isolated full-body lateral profile on a black background. Scanner-eligible.',
  },
  {
    id: 'commons-ltshears-toxotes-jaculatrix',
    canonicalSpeciesId: 'archerfish',
    scientificName: 'Toxotes jaculatrix',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farcherfish%2Fcommons-ltshears-toxotes-jaculatrix%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farcherfish%2Fcommons-ltshears-toxotes-jaculatrix%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farcherfish%2Fcommons-ltshears-toxotes-jaculatrix%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Farcherfish%2Fcommons-ltshears-toxotes-jaculatrix%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:ArcherFish.jpg',
    photographer: 'Ltshears',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:ArcherFish.jpg',
    creditText: 'Ltshears / Wikimedia Commons · Public Domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Toxotes jaculatrix reference attached to Roddo’s broader Archerfish (Toxotes spp.) profile. Representative display image only; scanner evidence stays disabled because the catalog profile is genus-level.',
  },
  {
    id: 'noaa-chaetodipterus-faber-historic',
    canonicalSpeciesId: 'spadefish',
    scientificName: 'Chaetodipterus faber',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspadefish%2Fnoaa-chaetodipterus-faber-historic%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspadefish%2Fnoaa-chaetodipterus-faber-historic%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspadefish%2Fnoaa-chaetodipterus-faber-historic%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspadefish%2Fnoaa-chaetodipterus-faber-historic%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'National Oceanic and Atmospheric Administration',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Chaetodipterus_faber.jpg',
    photographer: 'NOAA Photo Library',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Chaetodipterus_faber.jpg',
    creditText: 'NOAA Photo Library · Public Domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free historical Atlantic Spadefish morphology illustration. Strong reference for the species card, but illustration context keeps it display-only for scanner evidence.',
  },
  {
    id: 'ifremer-00764-87617-bigeye-tuna',
    canonicalSpeciesId: 'bigeye_tuna',
    scientificName: 'Thunnus obesus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigeye_tuna%2Fifremer-00764-87617-bigeye-tuna%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigeye_tuna%2Fifremer-00764-87617-bigeye-tuna%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigeye_tuna%2Fifremer-00764-87617-bigeye-tuna%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigeye_tuna%2Fifremer-00764-87617-bigeye-tuna%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'IFREMER',
    sourceName: 'IFREMER / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Thon_ob%C3%A8se_(Thunnus_obesus)_(Ifremer_00764-87617).jpg',
    photographer: 'Hugues Evano / IFREMER',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Hugues Evano / IFREMER · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Bigeye Tuna specimen/reference image from IFREMER. Exact species match; retained as display-only because it is a sampling/laboratory reference rather than a natural-scene scanner exemplar.',
  },
  {
    id: 'qld-archives-frank-olsen-black-marlin-1965',
    canonicalSpeciesId: 'black_marlin',
    scientificName: 'Istiompax indica',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_marlin%2Fqld-archives-frank-olsen-black-marlin-1965%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_marlin%2Fqld-archives-frank-olsen-black-marlin-1965%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_marlin%2Fqld-archives-frank-olsen-black-marlin-1965%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblack_marlin%2Fqld-archives-frank-olsen-black-marlin-1965%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Queensland State Archives',
    sourceName: 'Queensland State Archives via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Black_Marlin_Artwork_by_Frank_Olsen.jpg',
    photographer: 'Frank Olsen / Queensland State Archives',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Black_Marlin_Artwork_by_Frank_Olsen.jpg',
    creditText: 'Frank Olsen / Queensland State Archives · Public Domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free 1965 Black Marlin artwork with exact Istiompax indica identity. Used as a trusted display reference only; illustration context is excluded from scanner evidence.',
  },
  {
    id: 'ifremer-00761-87277-sailfish',
    canonicalSpeciesId: 'sailfish',
    scientificName: 'Istiophorus platypterus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsailfish%2Fifremer-00761-87277-sailfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsailfish%2Fifremer-00761-87277-sailfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsailfish%2Fifremer-00761-87277-sailfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsailfish%2Fifremer-00761-87277-sailfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'IFREMER',
    sourceName: 'IFREMER / Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Voilier_de_l%27Indo-Pacifique_(Istiophorus_platypterus)_(Ifremer_00761-87277).jpg',
    photographer: 'Fanchon Varenne / IFREMER',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Fanchon Varenne / IFREMER · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Indo-Pacific Sailfish specimen/reference image from IFREMER. Exact species match; display-only because it originates from field sampling/laboratory research context.',
  },
  {
    id: 'inat-sthomas8-alabama-bass',
    canonicalSpeciesId: 'alabama_bass',
    scientificName: 'Micropterus henshalli',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falabama_bass%2Finat-sthomas8-alabama-bass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falabama_bass%2Finat-sthomas8-alabama-bass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falabama_bass%2Finat-sthomas8-alabama-bass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Falabama_bass%2Finat-sthomas8-alabama-bass%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'iNaturalist',
    sourceName: 'iNaturalist Open Data',
    sourceUrl: 'https://www.inaturalist.org/taxa/225143-Micropterus-henshalli',
    photographer: 'sthomas8',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'sthomas8 / iNaturalist · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free juvenile Alabama Bass reference with a clean lateral profile. Juvenile life stage keeps it display-only for scanner evidence.',
  },
  {
    id: 'noaa-schmahl-greater-amberjack',
    canonicalSpeciesId: 'amberjack_sea',
    scientificName: 'Seriola dumerili',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famberjack_sea%2Fnoaa-schmahl-greater-amberjack%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famberjack_sea%2Fnoaa-schmahl-greater-amberjack%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famberjack_sea%2Fnoaa-schmahl-greater-amberjack%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famberjack_sea%2Fnoaa-schmahl-greater-amberjack%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA Sanctuaries Collection via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Seriola_dumerili_-_Sanc2431.jpg',
    photographer: 'G. P. Schmahl / Flower Garden Banks NMS',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'G. P. Schmahl / NOAA Flower Garden Banks NMS · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Greater Amberjack underwater reference used as a representative for the generic Amberjack profile (Seriola spp.). Display-only because catalog taxon is broader than the exact photographed species.',
  },
  {
    id: 'nybs-edmonson-chrisp-golden-redhorse',
    canonicalSpeciesId: 'golden_redhorse',
    scientificName: 'Moxostoma erythrurum',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_redhorse%2Fnybs-edmonson-chrisp-golden-redhorse%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_redhorse%2Fnybs-edmonson-chrisp-golden-redhorse%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_redhorse%2Fnybs-edmonson-chrisp-golden-redhorse%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_redhorse%2Fnybs-edmonson-chrisp-golden-redhorse%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'New York Biological Survey',
    sourceName: 'New York State Conservation Department via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Moxostoma_erythrurum.jpg',
    photographer: 'Ellen Edmonson and Hugh Chrisp',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Moxostoma_erythrurum.jpg',
    creditText: 'Ellen Edmonson & Hugh Chrisp / New York Biological Survey',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free historical Golden Redhorse illustration from the New York Biological Survey. Strong display reference; illustration is not scanner evidence.',
  },
  {
    id: 'commons-laslo-ilyes-golden-trevally',
    canonicalSpeciesId: 'golden_trevally',
    scientificName: 'Gnathanodon speciosus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_trevally%2Fcommons-laslo-ilyes-golden-trevally%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_trevally%2Fcommons-laslo-ilyes-golden-trevally%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_trevally%2Fcommons-laslo-ilyes-golden-trevally%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgolden_trevally%2Fcommons-laslo-ilyes-golden-trevally%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / Flickr',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Gnathanodon_speciosus.jpg',
    photographer: 'Laszlo Ilyes',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Laszlo Ilyes / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural Golden Trevally school. Excellent display reference; multiple fish keep it out of scanner evidence.',
  },
  {
    id: 'noaa-sefsc-gray-triggerfish',
    canonicalSpeciesId: 'gray_triggerfish',
    scientificName: 'Balistes capriscus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgray_triggerfish%2Fnoaa-sefsc-gray-triggerfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgray_triggerfish%2Fnoaa-sefsc-gray-triggerfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgray_triggerfish%2Fnoaa-sefsc-gray-triggerfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgray_triggerfish%2Fnoaa-sefsc-gray-triggerfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Balistes_capriscus_NOAA.jpg',
    photographer: 'SEFSC Pascagoula Laboratory / Brandi Noble collection',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'NOAA/NMFS/SEFSC / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free isolated Gray Triggerfish reference with strong full-body diagnostic morphology. Scanner-eligible.',
  },
  {
    id: 'nybs-edmonson-chrisp-grass-pickerel',
    canonicalSpeciesId: 'grass_pickerel',
    scientificName: 'Esox americanus vermiculatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrass_pickerel%2Fnybs-edmonson-chrisp-grass-pickerel%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrass_pickerel%2Fnybs-edmonson-chrisp-grass-pickerel%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrass_pickerel%2Fnybs-edmonson-chrisp-grass-pickerel%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgrass_pickerel%2Fnybs-edmonson-chrisp-grass-pickerel%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'New York State Conservation Department',
    sourceName: 'New York Biological Survey via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Esox_americanus_vermiculatus.jpg',
    photographer: 'Ellen Edmonson and Hugh Chrisp',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Esox_americanus_vermiculatus.jpg',
    creditText: 'Ellen Edmonson & Hugh Chrisp / New York Biological Survey',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free historical Grass Pickerel plate. Excellent morphology reference, but illustration/reference-plate context keeps it display-only.',
  },
  {
    id: 'commons-bedo-jobfish',
    canonicalSpeciesId: 'jobfish',
    scientificName: 'Aprion virescens',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjobfish%2Fcommons-bedo-jobfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjobfish%2Fcommons-bedo-jobfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjobfish%2Fcommons-bedo-jobfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjobfish%2Fcommons-bedo-jobfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Aprion_virescens.jpg',
    photographer: 'BEDO (Thailand)',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'BEDO (Thailand) / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free full-body Jobfish reference on a clean blue background. Strong lateral morphology and scanner-eligible.',
  },
  {
    id: 'commons-davesne-sargo',
    canonicalSpeciesId: 'sargo',
    scientificName: 'Anisotremus davidsonii',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsargo%2Fcommons-davesne-sargo%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsargo%2Fcommons-davesne-sargo%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsargo%2Fcommons-davesne-sargo%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsargo%2Fcommons-davesne-sargo%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'iNaturalist via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Anisotremus_davidsonii_231885543.jpg',
    photographer: 'Donald Davesne',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Donald Davesne / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free underwater Sargo reference with a clear full-body lateral profile. Scanner-eligible.',
  },
  {
    id: 'commons-davesne-topmelt',
    canonicalSpeciesId: 'topmelt',
    scientificName: 'Atherinops affinis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftopmelt%2Fcommons-davesne-topmelt%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftopmelt%2Fcommons-davesne-topmelt%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftopmelt%2Fcommons-davesne-topmelt%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftopmelt%2Fcommons-davesne-topmelt%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'iNaturalist via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atherinops_affinis_236788565.jpg',
    photographer: 'Donald Davesne',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Donald Davesne / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural Topmelt school. Several clear fish are visible, but multiple individuals keep it display-only for scanner evidence.',
  },
  {
    id: 'cdfg-yellowfin-croaker',
    canonicalSpeciesId: 'yellowfin_croaker',
    scientificName: 'Umbrina roncador',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_croaker%2Fcdfg-yellowfin-croaker%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_croaker%2Fcdfg-yellowfin-croaker%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_croaker%2Fcdfg-yellowfin-croaker%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_croaker%2Fcdfg-yellowfin-croaker%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'California Department of Fish and Game',
    sourceName: 'California Department of Fish and Game via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Umbrina_roncador_mspc098.jpg',
    photographer: 'California Department of Fish and Game',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Umbrina_roncador_mspc098.jpg',
    creditText: 'California Department of Fish and Game',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free public-domain Yellowfin Croaker reference. Clean lateral morphology, but low source resolution keeps it display-only.',
  },
  {
    id: 'commons-cricket-raspet-mayan-cichlid',
    canonicalSpeciesId: 'maya_cichlid',
    scientificName: 'Mayaheros urophthalmus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmaya_cichlid%2Fcommons-cricket-raspet-mayan-cichlid%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmaya_cichlid%2Fcommons-cricket-raspet-mayan-cichlid%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmaya_cichlid%2Fcommons-cricket-raspet-mayan-cichlid%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmaya_cichlid%2Fcommons-cricket-raspet-mayan-cichlid%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'iNaturalist via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Mayaheros_urophthalmus_261444461.jpg',
    photographer: 'Cricket Raspet',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Cricket Raspet / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural Mayan Cichlid reference with a clear full-body profile. Scanner-eligible.',
  },
  {
    id: 'commons-gant223-giant-freshwater-stingray',
    canonicalSpeciesId: 'giant_freshwater_stingray',
    scientificName: 'Urogymnus polylepis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_freshwater_stingray%2Fcommons-gant223-giant-freshwater-stingray%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_freshwater_stingray%2Fcommons-gant223-giant-freshwater-stingray%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_freshwater_stingray%2Fcommons-gant223-giant-freshwater-stingray%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgiant_freshwater_stingray%2Fcommons-gant223-giant-freshwater-stingray%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Urogymnus_polylepis_at_Itabashi_Botanical_Garden.png',
    photographer: 'Gant223',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Gant223 / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium Giant Freshwater Stingray reference. Other fish are present in the background and this is an education-sensitive species, so display-only for scanner evidence.',
  },
  {
    id: 'commons-justine-ruby-snapper',
    canonicalSpeciesId: 'ruby_snapper',
    scientificName: 'Etelis carbunculus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fruby_snapper%2Fcommons-justine-ruby-snapper%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fruby_snapper%2Fcommons-justine-ruby-snapper%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fruby_snapper%2Fcommons-justine-ruby-snapper%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fruby_snapper%2Fcommons-justine-ruby-snapper%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Etelis_carbunculus_JNC2427_(Lutjanidae).JPG',
    photographer: 'Jean-Lou Justine',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Jean-Lou Justine / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free exact Etelis carbunculus specimen used as the representative image for Roddo\'s broader Ruby Snapper profile. Excellent morphology; specimen context keeps it display-only.',
  },
  {
    id: 'commons-ranjith-wallago-attu',
    canonicalSpeciesId: 'wallago_catfish',
    scientificName: 'Wallago attu',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwallago_catfish%2Fcommons-ranjith-wallago-attu%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwallago_catfish%2Fcommons-ranjith-wallago-attu%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwallago_catfish%2Fcommons-ranjith-wallago-attu%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwallago_catfish%2Fcommons-ranjith-wallago-attu%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Wallago_attu.jpg',
    photographer: 'Ranjith-chemmad',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Ranjith-chemmad / Wikimedia Commons · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Wallago attu field/specimen reference used for Roddo\'s broader Wallago Catfish profile. Clear full-body morphology; display-only for scanner evidence.',
  },
  {
    id: 'noaa-midnight-parrotfish',
    canonicalSpeciesId: 'parrotfish_sea',
    scientificName: 'Scarus coelestinus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fparrotfish_sea%2Fnoaa-midnight-parrotfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fparrotfish_sea%2Fnoaa-midnight-parrotfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fparrotfish_sea%2Fnoaa-midnight-parrotfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fparrotfish_sea%2Fnoaa-midnight-parrotfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'Florida Keys National Marine Sanctuary via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Midnight_parrotfish.jpg',
    photographer: 'Kevin Hogan',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Midnight_parrotfish.jpg',
    creditText: 'Kevin Hogan / Florida Keys National Marine Sanctuary / NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Midnight Parrotfish (Scarus coelestinus) used as a representative visual for Roddo\'s broad Parrotfish / Scaridae profile. Display-only because the catalog entry is a family-level group.',
  },
  {
    id: 'noaa-atlantic-blue-marlin-representative',
    canonicalSpeciesId: 'marlin',
    scientificName: 'Istiophoridae spp.',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmarlin%2Fnoaa-atlantic-blue-marlin-representative%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmarlin%2Fnoaa-atlantic-blue-marlin-representative%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmarlin%2Fnoaa-atlantic-blue-marlin-representative%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmarlin%2Fnoaa-atlantic-blue-marlin-representative%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'NOAA via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_blue_marlin.jpg',
    photographer: 'NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Atlantic_blue_marlin.jpg',
    creditText: 'NOAA / Wikimedia Commons',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Atlantic Blue Marlin used as a representative visual for Roddo\'s broad Marlin / Istiophoridae profile. Display-only because the catalog entry is a family-level group.',
  },
  {
    id: 'commons-davesne-spotfin-croaker',
    canonicalSpeciesId: 'spotfin_croaker',
    scientificName: 'Roncador stearnsii',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotfin_croaker%2Fcommons-davesne-spotfin-croaker%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotfin_croaker%2Fcommons-davesne-spotfin-croaker%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotfin_croaker%2Fcommons-davesne-spotfin-croaker%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotfin_croaker%2Fcommons-davesne-spotfin-croaker%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'iNaturalist via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Roncador_stearnsii_La_Jolla.jpg',
    photographer: 'Donald Davesne',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Donald Davesne / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free natural Spotfin Croaker reference from La Jolla with the full fish visible in shallow water. Scanner-eligible.',
  },
  {
    id: 'uw-fmib-zebra-perch',
    canonicalSpeciesId: 'zebra_perch',
    scientificName: 'Hermosilla azurea',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzebra_perch%2Fuw-fmib-zebra-perch%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzebra_perch%2Fuw-fmib-zebra-perch%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzebra_perch%2Fuw-fmib-zebra-perch%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fzebra_perch%2Fuw-fmib-zebra-perch%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'University of Washington',
    sourceName: 'Freshwater and Marine Image Bank via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:FMIB_33922_Hermosilla_Azurea_Jenkins_and_Evermann.jpeg',
    photographer: 'David Starr Jordan / Freshwater and Marine Image Bank',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:FMIB_33922_Hermosilla_Azurea_Jenkins_and_Evermann.jpeg',
    creditText: 'Freshwater and Marine Image Bank / University of Washington',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free public-domain historical Zebra Perch illustration. Excellent morphology reference; illustration context keeps it display-only.',
  },
  {
    id: 'commons-almbauer-asian-arowana',
    canonicalSpeciesId: 'asian_arowana',
    scientificName: 'Scleropages formosus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fasian_arowana%2Fcommons-almbauer-asian-arowana%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fasian_arowana%2Fcommons-almbauer-asian-arowana%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fasian_arowana%2Fcommons-almbauer-asian-arowana%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fasian_arowana%2Fcommons-almbauer-asian-arowana%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Asiatischer_Gabelbart_(Scleropages_formosus).jpg',
    photographer: 'Marco Almbauer',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Asiatischer_Gabelbart_(Scleropages_formosus).jpg',
    creditText: 'Marco Almbauer / Wikimedia Commons · Public Domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free Asian Arowana aquarium reference. Roddo marks this species education-sensitive; display-only for scanner evidence.',
  },
  {
    id: 'commons-jesaya-li-clown-featherback',
    canonicalSpeciesId: 'clown_featherback',
    scientificName: 'Chitala ornata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fclown_featherback%2Fcommons-jesaya-li-clown-featherback%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fclown_featherback%2Fcommons-jesaya-li-clown-featherback%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fclown_featherback%2Fcommons-jesaya-li-clown-featherback%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fclown_featherback%2Fcommons-jesaya-li-clown-featherback%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Adult_Clown_featherback_in_aquarium.jpg',
    photographer: 'Jesaya Li',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Jesaya Li · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium side-profile reference. Exact Chitala ornata identity on source page. Display/reference only because another fish overlaps the frame.',
  },
  {
    id: 'usfws-21510-fallfish',
    canonicalSpeciesId: 'fallfish',
    scientificName: 'Semotilus corporalis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffallfish%2Fusfws-21510-fallfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffallfish%2Fusfws-21510-fallfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffallfish%2Fusfws-21510-fallfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ffallfish%2Fusfws-21510-fallfish%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/semotilus-corporalis',
    photographer: 'Patterson, Matthew/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/semotilus-corporalis',
    creditText: 'Patterson, Matthew/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain NCTC teaching-collection specimen. Exact Semotilus corporalis identity. Strong morphology display reference; preserved coloration keeps it out of scanner evidence.',
  },
  {
    id: 'usfws-903a9159-redeye-bass',
    canonicalSpeciesId: 'redeye_bass',
    scientificName: 'Micropterus coosae',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredeye_bass%2Fusfws-903a9159-redeye-bass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredeye_bass%2Fusfws-903a9159-redeye-bass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredeye_bass%2Fusfws-903a9159-redeye-bass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredeye_bass%2Fusfws-903a9159-redeye-bass%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/coosa-bass',
    photographer: 'Hagerty, Ryan/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/coosa-bass',
    creditText: 'Hagerty, Ryan/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Public-domain underwater Coosa/Redeye Bass reference. Source identifies Micropterus coosae. Clear natural lateral body pattern; scanner-eligible after conservative visual audit.',
  },
  {
    id: 'commons-david-stang-sheepshead-2zz',
    canonicalSpeciesId: 'sheepshead',
    scientificName: 'Archosargus probatocephalus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fcommons-david-stang-sheepshead-2zz%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fcommons-david-stang-sheepshead-2zz%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fcommons-david-stang-sheepshead-2zz%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsheepshead%2Fcommons-david-stang-sheepshead-2zz%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Archosargus_probatocephalus_2zz.jpg',
    photographer: 'David J. Stang',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'David J. Stang · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium photo with diagnostic vertical bars. Exact Archosargus probatocephalus identity. Replaces the prior held-out human/catch photo for display; scanner remains conservative.',
  },
  {
    id: 'commons-bloopityboop-shiner-perch-1',
    canonicalSpeciesId: 'shiner_perch',
    scientificName: 'Cymatogaster aggregata',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshiner_perch%2Fcommons-bloopityboop-shiner-perch-1%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshiner_perch%2Fcommons-bloopityboop-shiner-perch-1%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshiner_perch%2Fcommons-bloopityboop-shiner-perch-1%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshiner_perch%2Fcommons-bloopityboop-shiner-perch-1%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cymatogaster_aggregata_1.jpg',
    photographer: 'Bloopityboop',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    creditText: 'Bloopityboop · CC BY-SA 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Human-free aquarium side profile. Exact Cymatogaster aggregata identity. Display reference only because another fish overlaps below the primary subject.',
  },
  {
    id: 'commons-daniel-estabrooks-florida-bass',
    canonicalSpeciesId: 'florida_strain_largemouth',
    scientificName: 'Micropterus floridanus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflorida_strain_largemouth%2Fcommons-daniel-estabrooks-florida-bass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflorida_strain_largemouth%2Fcommons-daniel-estabrooks-florida-bass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflorida_strain_largemouth%2Fcommons-daniel-estabrooks-florida-bass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fflorida_strain_largemouth%2Fcommons-daniel-estabrooks-florida-bass%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Florida_bass.jpg',
    photographer: 'Daniel Estabrooks',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Daniel Estabrooks · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Florida Bass identity on the source page as Micropterus floridanus. Roddo catalog currently retains legacy subspecies wording Micropterus salmoides floridanus. Human-free caught/net photo; display reference only.',
  },
  {
    id: 'noaa-lutjanus-bohar',
    canonicalSpeciesId: 'red_bass_snapper',
    scientificName: 'Lutjanus bohar',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_bass_snapper%2Fnoaa-lutjanus-bohar%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_bass_snapper%2Fnoaa-lutjanus-bohar%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_bass_snapper%2Fnoaa-lutjanus-bohar%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_bass_snapper%2Fnoaa-lutjanus-bohar%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA',
    sourceName: 'Wikimedia Commons mirror of NOAA material',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_bohar.jpg',
    photographer: 'NOAA',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Lutjanus_bohar.jpg',
    creditText: 'NOAA',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Lutjanus bohar identity; source page states NOAA public-domain federal material. Clean underwater lateral reference with strong morphology; scanner-eligible after conservative visual audit.',
  },
  {
    id: 'commons-andrew-deacon-redbreast-tilapia',
    canonicalSpeciesId: 'redbreast_tilapia',
    scientificName: 'Coptodon rendalli',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredbreast_tilapia%2Fcommons-andrew-deacon-redbreast-tilapia%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredbreast_tilapia%2Fcommons-andrew-deacon-redbreast-tilapia%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredbreast_tilapia%2Fcommons-andrew-deacon-redbreast-tilapia%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredbreast_tilapia%2Fcommons-andrew-deacon-redbreast-tilapia%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Coptodon_rendalli_127701747.jpg',
    photographer: 'Andrew Deacon',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Andrew Deacon · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Coptodon rendalli identity and reviewed CC0 license on source page. Clear full-body field photo, but human hand/background keeps it display-only.',
  },
  {
    id: 'usgs-2024-shoal-bass',
    canonicalSpeciesId: 'shoal_bass',
    scientificName: 'Micropterus cataractae',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshoal_bass%2Fusgs-2024-shoal-bass%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshoal_bass%2Fusgs-2024-shoal-bass%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshoal_bass%2Fusgs-2024-shoal-bass%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshoal_bass%2Fusgs-2024-shoal-bass%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Geological Survey',
    sourceName: 'USGS Multimedia Gallery',
    sourceUrl: 'https://www.usgs.gov/media/images/shoal-bass',
    photographer: 'USGS Alabama Cooperative Fish and Wildlife Research Unit',
    rights: 'public-domain',
    rightsUrl: 'https://www.usgs.gov/media/images/shoal-bass',
    creditText: 'USGS Alabama Cooperative Fish and Wildlife Research Unit',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Micropterus cataractae identity. USGS source page explicitly states Public Domain. Strong field reference but human-held/caught context keeps it display-only.',
  },
  {
    id: 'commons-staticd-pangasianodon-hypophthalmus',
    canonicalSpeciesId: 'striped_catfish_patin',
    scientificName: 'Pangasianodon hypophthalmus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_catfish_patin%2Fcommons-staticd-pangasianodon-hypophthalmus%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_catfish_patin%2Fcommons-staticd-pangasianodon-hypophthalmus%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_catfish_patin%2Fcommons-staticd-pangasianodon-hypophthalmus%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstriped_catfish_patin%2Fcommons-staticd-pangasianodon-hypophthalmus%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Pangasianodon_hypophthalmus.jpg',
    photographer: 'Staticd',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    creditText: 'Staticd · CC BY-SA 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Source page documents morphology-based identification as Pangasianodon hypophthalmus and CC BY-SA 3.0. Roddo profile is a broader Patin group entry; this exact-species image is representative/display-only and not scanner evidence.',
  },
  {
    id: 'commons-andy-kleinhesselink-barred-surfperch',
    canonicalSpeciesId: 'barred_surfperch',
    scientificName: 'Amphistichus argenteus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_surfperch%2Fcommons-andy-kleinhesselink-barred-surfperch%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_surfperch%2Fcommons-andy-kleinhesselink-barred-surfperch%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_surfperch%2Fcommons-andy-kleinhesselink-barred-surfperch%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbarred_surfperch%2Fcommons-andy-kleinhesselink-barred-surfperch%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Amphistichus_argenteus_94917951.jpg',
    photographer: 'Andy Kleinhesselink',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Andy Kleinhesselink · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Barred Surfperch (Amphistichus argenteus) identity on the source page. Reviewed iNaturalist-to-Commons CC0 license. Caught/hook context keeps this reference display-only.',
  },
  {
    id: 'usfws-sam-stukel-bigmouth-buffalo-juvenile',
    canonicalSpeciesId: 'bigmouth_buffalo',
    scientificName: 'Ictiobus cyprinellus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigmouth_buffalo%2Fusfws-sam-stukel-bigmouth-buffalo-juvenile%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigmouth_buffalo%2Fusfws-sam-stukel-bigmouth-buffalo-juvenile%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigmouth_buffalo%2Fusfws-sam-stukel-bigmouth-buffalo-juvenile%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbigmouth_buffalo%2Fusfws-sam-stukel-bigmouth-buffalo-juvenile%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'juvenile',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/bigmouth-buffalo-juvenile-0',
    photographer: 'Sam Stukel/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/bigmouth-buffalo-juvenile-0',
    creditText: 'Sam Stukel/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Ictiobus cyprinellus identity and Public Domain license are stated on the FWS source page. Human-free clean lateral juvenile reference; juvenile life stage keeps it display-only for scanner trust.',
  },
  {
    id: 'usfws-matthew-patterson-creek-chub',
    canonicalSpeciesId: 'creek_chub',
    scientificName: 'Semotilus atromaculatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcreek_chub%2Fusfws-matthew-patterson-creek-chub%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcreek_chub%2Fusfws-matthew-patterson-creek-chub%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcreek_chub%2Fusfws-matthew-patterson-creek-chub%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcreek_chub%2Fusfws-matthew-patterson-creek-chub%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library / NCTC Freshwater Fishes Teaching Collection',
    sourceUrl: 'https://www.fws.gov/media/creek-chub',
    photographer: 'Patterson, Matthew/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/creek-chub',
    creditText: 'Patterson, Matthew/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Semotilus atromaculatus identity and Public Domain license are stated on the FWS page. Preserved teaching-collection specimen gives useful morphology but altered specimen context keeps it display-only.',
  },
  {
    id: 'commons-simoes-zarco-moreno-hogfish-madagascar-reef',
    canonicalSpeciesId: 'hogfish',
    scientificName: 'Lachnolaimus maximus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhogfish%2Fcommons-simoes-zarco-moreno-hogfish-madagascar-reef%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhogfish%2Fcommons-simoes-zarco-moreno-hogfish-madagascar-reef%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhogfish%2Fcommons-simoes-zarco-moreno-hogfish-madagascar-reef%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhogfish%2Fcommons-simoes-zarco-moreno-hogfish-madagascar-reef%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Biodiversity Data Journal via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lachnolaimus_maximus_in_Madagascar_Reef.jpg',
    photographer: 'Simões, N.; Zarco Perello, S.; Moreno Mendoza, R.',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Simões, Zarco Perello & Moreno Mendoza · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Lachnolaimus maximus reef photograph. Source page credits the 2014 Biodiversity Data Journal work and licenses the image CC BY 4.0. Human-free underwater full-body reference with strong morphology; scanner-eligible after conservative visual audit.',
  },
  {
    id: 'commons-ruff-tuff-cream-puff-jacksmelt-pair',
    canonicalSpeciesId: 'jacksmelt',
    scientificName: 'Atherinopsis californiensis',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjacksmelt%2Fcommons-ruff-tuff-cream-puff-jacksmelt-pair%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjacksmelt%2Fcommons-ruff-tuff-cream-puff-jacksmelt-pair%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjacksmelt%2Fcommons-ruff-tuff-cream-puff-jacksmelt-pair%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjacksmelt%2Fcommons-ruff-tuff-cream-puff-jacksmelt-pair%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Atherinopsis_californiensis_pair.jpg',
    photographer: 'Ruff tuff cream puff',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Ruff tuff cream puff · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Atherinopsis californiensis identity; source page states the pair was caught in San Diego Bay and released under CC0 1.0. Human-free close pair image is useful for display, but framing does not show clean full bodies for scanner evidence.',
  },
  {
    id: 'commons-sannidhishetty-silver-striped-catfish',
    canonicalSpeciesId: 'iridescent_shark',
    scientificName: 'Pangasianodon hypophthalmus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Firidescent_shark%2Fcommons-sannidhishetty-silver-striped-catfish%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Firidescent_shark%2Fcommons-sannidhishetty-silver-striped-catfish%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Firidescent_shark%2Fcommons-sannidhishetty-silver-striped-catfish%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Firidescent_shark%2Fcommons-sannidhishetty-silver-striped-catfish%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Silver_striped_Catfish_(Pangasius_hypophthalmus).jpg',
    photographer: 'Sannidhishetty',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Sannidhishetty · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Source page identifies Pangasianodon hypophthalmus (older Pangasius naming appears in the filename) and the uploader dedicates the photograph under CC0 1.0. Market/caught presentation with two fish and incomplete clean lateral isolation; display-only.',
  },
  {
    id: 'commons-amicecorp-probarbus-jullieni',
    canonicalSpeciesId: 'julliens_golden_carp',
    scientificName: 'Probarbus jullieni',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjulliens_golden_carp%2Fcommons-amicecorp-probarbus-jullieni%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjulliens_golden_carp%2Fcommons-amicecorp-probarbus-jullieni%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjulliens_golden_carp%2Fcommons-amicecorp-probarbus-jullieni%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjulliens_golden_carp%2Fcommons-amicecorp-probarbus-jullieni%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'juvenile',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Probarbus_jullieni.JPG',
    photographer: 'Amicecorp',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Probarbus_jullieni.JPG',
    creditText: 'Amicecorp · Public domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Probarbus jullieni identity on the source page; copyright holder released the work into the public domain. Aquaculture/juvenile specimen photographed out of natural water, so it remains display-only for scanner trust.',
  },
  {
    id: 'commons-bob-miller-bay-scallop-465415494',
    canonicalSpeciesId: 'bay_scallop',
    scientificName: 'Argopecten irradians',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbay_scallop%2Fcommons-bob-miller-bay-scallop-465415494%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbay_scallop%2Fcommons-bob-miller-bay-scallop-465415494%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbay_scallop%2Fcommons-bob-miller-bay-scallop-465415494%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fbay_scallop%2Fcommons-bob-miller-bay-scallop-465415494%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Argopecten_irradians_465415494.jpg',
    photographer: 'Bob Miller',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Bob Miller · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Atlantic Bay Scallop (Argopecten irradians) identity and reviewed iNaturalist-to-Commons CC BY 4.0 license. Shell is shown on a human hand; useful for display and morphology but intentionally excluded from scanner evidence.',
  },
  {
    id: 'noaa-guy-fanguy-blue-crab-louisiana',
    canonicalSpeciesId: 'blue_crab',
    scientificName: 'Callinectes sapidus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_crab%2Fnoaa-guy-fanguy-blue-crab-louisiana%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_crab%2Fnoaa-guy-fanguy-blue-crab-louisiana%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_crab%2Fnoaa-guy-fanguy-blue-crab-louisiana%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_crab%2Fnoaa-guy-fanguy-blue-crab-louisiana%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'National Oceanic and Atmospheric Administration',
    sourceName: 'NOAA image via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Callinectes_sapidus,_Louisiana.jpg',
    photographer: 'NOAA / Guy Fanguy',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Callinectes_sapidus,_Louisiana.jpg',
    creditText: 'NOAA / Guy Fanguy · Public domain',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Callinectes sapidus identity. Commons source page explicitly states the NOAA work is public domain because it was made as part of official duties. Human-free live-water image, but the body is partly cropped/overlapped, so scanner eligibility remains false.',
  },
  {
    id: 'commons-ritirene-american-lobster-254081762',
    canonicalSpeciesId: 'american_lobster',
    scientificName: 'Homarus americanus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famerican_lobster%2Fcommons-ritirene-american-lobster-254081762%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famerican_lobster%2Fcommons-ritirene-american-lobster-254081762%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famerican_lobster%2Fcommons-ritirene-american-lobster-254081762%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Famerican_lobster%2Fcommons-ritirene-american-lobster-254081762%2Foriginal.webp?alt=media',
    angle: 'top',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Homarus_americanus_254081762.jpg',
    photographer: 'ritirene',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'ritirene · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact American Lobster (Homarus americanus) identity and reviewed iNaturalist-to-Commons CC BY 4.0 license. Human-free, full-body natural-context dorsal view with strong diagnostic morphology; scanner-eligible after conservative visual audit.',
  },
  {
    id: 'usfws-20956-smallmouth-buffalo',
    canonicalSpeciesId: 'smallmouth_buffalo',
    scientificName: 'Ictiobus bubalus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_buffalo%2Fusfws-20956-smallmouth-buffalo%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_buffalo%2Fusfws-20956-smallmouth-buffalo%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_buffalo%2Fusfws-20956-smallmouth-buffalo%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsmallmouth_buffalo%2Fusfws-20956-smallmouth-buffalo%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/smallmouth-buffalo',
    photographer: 'Patterson, Matthew/USFWS',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/smallmouth-buffalo',
    creditText: 'Patterson, Matthew/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Ictiobus bubalus identity on the FWS source page; Media Usage Rights/License explicitly states Public Domain. NCTC Freshwater Fishes Teaching Collection preserved specimen; useful morphology display reference but preserved coloration/context keeps scanner eligibility false.',
  },
  {
    id: 'usfws-5113-round-goby',
    canonicalSpeciesId: 'round_goby',
    scientificName: 'Neogobius melanostomus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fround_goby%2Fusfws-5113-round-goby%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fround_goby%2Fusfws-5113-round-goby%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fround_goby%2Fusfws-5113-round-goby%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fround_goby%2Fusfws-5113-round-goby%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: 'https://www.fws.gov/media/round-goby',
    photographer: 'Engbretson, Eric',
    rights: 'public-domain',
    rightsUrl: 'https://www.fws.gov/media/round-goby',
    creditText: 'Engbretson, Eric',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Neogobius melanostomus identity on the FWS source page; Media Usage Rights/License explicitly states Public Domain. Clean human-free lateral aquarium/reference view with diagnostic body shape and markings; scanner-eligible after conservative audit.',
  },
  {
    id: 'commons-bex-goreham-osmerus-mordax-364847253',
    canonicalSpeciesId: 'rainbow_smelt',
    scientificName: 'Osmerus mordax',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_smelt%2Fcommons-bex-goreham-osmerus-mordax-364847253%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_smelt%2Fcommons-bex-goreham-osmerus-mordax-364847253%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_smelt%2Fcommons-bex-goreham-osmerus-mordax-364847253%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_smelt%2Fcommons-bex-goreham-osmerus-mordax-364847253%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Osmerus_mordax_364847253.jpg',
    photographer: 'Bex Goreham',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'Bex Goreham · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Rainbow Smelt (Osmerus mordax) identity. Commons records the iNaturalist image as reviewed and licensed CC BY 4.0. Human-free natural underwater school, but multiple overlapping fish prevent clean isolated scanner evidence; display-only.',
  },
  {
    id: 'commons-jean-paul-boerekamps-panulirus-argus-469741576',
    canonicalSpeciesId: 'spiny_lobster',
    scientificName: 'Panulirus argus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspiny_lobster%2Fcommons-jean-paul-boerekamps-panulirus-argus-469741576%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspiny_lobster%2Fcommons-jean-paul-boerekamps-panulirus-argus-469741576%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspiny_lobster%2Fcommons-jean-paul-boerekamps-panulirus-argus-469741576%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspiny_lobster%2Fcommons-jean-paul-boerekamps-panulirus-argus-469741576%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Panulirus_argus_469741576.jpg',
    photographer: 'Jean-Paul Boerekamps',
    rights: 'public-domain',
    rightsUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    creditText: 'Jean-Paul Boerekamps · CC0 1.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Caribbean Spiny Lobster (Panulirus argus) identity. Commons records the iNaturalist image as reviewed CC0 1.0. Human-free natural reef context, but much of the body is obscured inside a crevice; display-only for scanner trust.',
  },
  {
    id: 'commons-oceanicadventures-menippe-mercenaria-296482609',
    canonicalSpeciesId: 'stone_crab',
    scientificName: 'Menippe mercenaria',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstone_crab%2Fcommons-oceanicadventures-menippe-mercenaria-296482609%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstone_crab%2Fcommons-oceanicadventures-menippe-mercenaria-296482609%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstone_crab%2Fcommons-oceanicadventures-menippe-mercenaria-296482609%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fstone_crab%2Fcommons-oceanicadventures-menippe-mercenaria-296482609%2Foriginal.webp?alt=media',
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons / iNaturalist',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Florida_Stone_Crab_(Menippe_mercenaria).jpg',
    photographer: 'oceanicadventures / Parsa Fard',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/4.0/',
    creditText: 'oceanicadventures · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Florida Stone Crab (Menippe mercenaria) identity. Commons states the iNaturalist image was reviewed under CC BY 4.0. Full animal is visible but held by a person and photographed ventrally; display/reference only, not scanner evidence.',
  },
  {
    id: "noaa-meadows-rainbow-runner",
    canonicalSpeciesId: 'rainbow_runner',
    scientificName: 'Elagatis bipinnulata',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_runner%2Fnoaa-meadows-rainbow-runner%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frainbow_runner%2Fnoaa-meadows-rainbow-runner%2Fthumb.webp?alt=media",
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA/NMFS/OPR',
    sourceName: 'NOAA Coral Kingdom Collection via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Elagatis_bipinnulata.jpg",
    photographer: 'Dr. Dwayne Meadows, NOAA/NMFS/OPR',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Elagatis_bipinnulata.jpg",
    creditText: 'Dr. Dwayne Meadows, NOAA/NMFS/OPR',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Elagatis bipinnulata. Underwater school image; excellent display reference but multiple overlapping fish make it display-only for scanner evidence.",
  },
  {
    id: "noaa-john-brooks-rubberlip-kelp",
    canonicalSpeciesId: 'rubberlip_seaperch',
    scientificName: 'Rhacochilus toxotes',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frubberlip_seaperch%2Fnoaa-john-brooks-rubberlip-kelp%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frubberlip_seaperch%2Fnoaa-john-brooks-rubberlip-kelp%2Fthumb.webp?alt=media",
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Channel Islands National Marine Sanctuary',
    sourceName: 'NOAA image via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Rhacochilus_toxotes,_en_Kelp.jpg",
    photographer: 'NOAA John Brooks',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Rhacochilus_toxotes,_en_Kelp.jpg",
    creditText: 'NOAA John Brooks',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Rhacochilus toxotes in kelp habitat. Human-free underwater reference; multiple fish and habitat overlap keep scanner eligibility conservative.",
  },
  {
    id: "noaa-glerl-quillback",
    canonicalSpeciesId: 'quillback',
    scientificName: 'Carpiodes cyprinus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fquillback%2Fnoaa-glerl-quillback%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fquillback%2Fnoaa-glerl-quillback%2Fthumb.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA Great Lakes Environmental Research Laboratory',
    sourceName: 'NOAA GLERL image via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Quillback.jpg",
    photographer: 'NOAA GLERL',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Quillback.jpg",
    creditText: 'NOAA Great Lakes Environmental Research Laboratory',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Carpiodes cyprinus public-domain NOAA illustration. Strong morphology/display reference; artwork is excluded from photo scanner evidence.",
  },
  {
    id: "commons-marcus-bullock-spotted-bay-bass",
    canonicalSpeciesId: 'spotted_bay_bass',
    scientificName: 'Paralabrax maculatofasciatus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bay_bass%2Fcommons-marcus-bullock-spotted-bay-bass%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fspotted_bay_bass%2Fcommons-marcus-bullock-spotted-bay-bass%2Fthumb.webp?alt=media",
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons / FishBase contributor',
    sourceName: 'Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Paralabrax_maculatofasciatus.jpg",
    photographer: 'Marcus Bullock',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/3.0/",
    creditText: 'Marcus Bullock, CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Paralabrax maculatofasciatus specimen from Newport Harbor. Licensed CC BY 3.0; caught/on-ground context is display-only.",
  },
  {
    id: "commons-malaika-chawla-queenfish-366338301",
    canonicalSpeciesId: 'queenfish',
    scientificName: 'Scomberoides commersonnianus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fqueenfish%2Fcommons-malaika-chawla-queenfish-366338301%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fqueenfish%2Fcommons-malaika-chawla-queenfish-366338301%2Fthumb.webp?alt=media",
    angle: 'caught',
    lifeStage: 'adult',
    sourceAgency: 'iNaturalist / Wikimedia Commons',
    sourceName: 'Wikimedia Commons (iNaturalist reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Scomberoides_commersonnianus_366338301.jpg",
    photographer: 'Malaika Mathew Chawla',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: 'Malaika Mathew Chawla, CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Scomberoides commersonnianus; Commons record was reviewed from iNaturalist as CC BY 4.0. Caught-fish view remains display-only.",
  },
  {
    id: "noaa-swfsc-red-abalone",
    canonicalSpeciesId: 'abalone',
    scientificName: 'Haliotis spp.',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fabalone%2Fnoaa-swfsc-red-abalone%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fabalone%2Fnoaa-swfsc-red-abalone%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fabalone%2Fnoaa-swfsc-red-abalone%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fabalone%2Fnoaa-swfsc-red-abalone%2Foriginal.webp?alt=media",
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA Fisheries / Southwest Fisheries Science Center',
    sourceName: 'Southwest Fisheries Science Center via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Haliotis_rufescens.jpeg",
    photographer: 'Southwest Fisheries Science Center, NOAA Fisheries Service',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Haliotis_rufescens.jpeg",
    creditText: 'Southwest Fisheries Science Center / NOAA Fisheries',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Red Abalone (Haliotis rufescens) for Roddo broad Haliotis spp. profile. Exact source identity and NOAA public-domain status verified. Representative group image is display-only.",
  },
  {
    id: "commons-don-loarie-red-swamp-crawfish",
    canonicalSpeciesId: 'crawfish',
    scientificName: 'Procambarus / Faxonius spp.',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcrawfish%2Fcommons-don-loarie-red-swamp-crawfish%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcrawfish%2Fcommons-don-loarie-red-swamp-crawfish%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcrawfish%2Fcommons-don-loarie-red-swamp-crawfish%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fcrawfish%2Fcommons-don-loarie-red-swamp-crawfish%2Foriginal.webp?alt=media",
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'iNaturalist / Wikimedia Commons',
    sourceName: 'Wikimedia Commons (iNaturalist reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Procambarus_clarkii_-_inat_161296348.jpg",
    photographer: 'Don Loarie',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: 'Don Loarie / Wikimedia Commons · CC BY 4.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Red Swamp Crayfish (Procambarus clarkii) for Roddo broad Procambarus / Faxonius spp. profile. Exact source identity and CC BY 4.0 verified. Representative group image is display-only.",
  },
  {
    id: "usfws-peter-pearsall-dungeness-crab",
    canonicalSpeciesId: 'dungeness_crab',
    scientificName: 'Metacarcinus magister',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdungeness_crab%2Fusfws-peter-pearsall-dungeness-crab%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdungeness_crab%2Fusfws-peter-pearsall-dungeness-crab%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdungeness_crab%2Fusfws-peter-pearsall-dungeness-crab%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fdungeness_crab%2Fusfws-peter-pearsall-dungeness-crab%2Foriginal.webp?alt=media",
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'USFWS Pacific Region via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Metacarcinus_magister_(23838012165).jpg",
    photographer: 'Peter Pearsall / USFWS',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Metacarcinus_magister_(23838012165).jpg",
    creditText: 'Peter Pearsall / U.S. Fish & Wildlife Service',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Metacarcinus magister. Human-free field reference; framing is close/cropped rather than an isolated full-body scanner view, so display-only.",
  },
  {
    id: "commons-serc-winter-flounder-s0892",
    canonicalSpeciesId: 'winter_flounder',
    scientificName: 'Pseudopleuronectes americanus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwinter_flounder%2Fcommons-serc-winter-flounder-s0892%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwinter_flounder%2Fcommons-serc-winter-flounder-s0892%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwinter_flounder%2Fcommons-serc-winter-flounder-s0892%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fwinter_flounder%2Fcommons-serc-winter-flounder-s0892%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Smithsonian Environmental Research Center',
    sourceName: 'Wikimedia Commons (Flickr-reviewed SERC photo)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Pseudopleuronectes_americanus_(S0892)_(12658628105).jpg",
    photographer: 'Robert Aguilar / Smithsonian Environmental Research Center',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: 'Robert Aguilar / Smithsonian Environmental Research Center · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Replacement for the V32 FDA filet/specimen reference. Exact Pseudopleuronectes americanus; whole fish, human-free. VIMS Trawl Survey specimen; scanner remains false.",
  },
  {
    id: "usfws-31879-shorthead-redhorse",
    canonicalSpeciesId: 'shorthead_redhorse',
    scientificName: 'Moxostoma macrolepidotum',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshorthead_redhorse%2Fusfws-31879-shorthead-redhorse%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshorthead_redhorse%2Fusfws-31879-shorthead-redhorse%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshorthead_redhorse%2Fusfws-31879-shorthead-redhorse%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshorthead_redhorse%2Fusfws-31879-shorthead-redhorse%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/shorthead-redhorse-sucker-3",
    photographer: 'Stukel, Sam/USFWS',
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/shorthead-redhorse-sucker-3",
    creditText: 'Stukel, Sam/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Moxostoma macrolepidotum. Whole fish and human-free. Clean reference image with water-surface/reflection artifact; display-only.",
  },
  {
    id: "usfws-23746-river-carpsucker",
    canonicalSpeciesId: 'river_carpsucker',
    scientificName: 'Carpiodes carpio',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Friver_carpsucker%2Fusfws-23746-river-carpsucker%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Friver_carpsucker%2Fusfws-23746-river-carpsucker%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Friver_carpsucker%2Fusfws-23746-river-carpsucker%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Friver_carpsucker%2Fusfws-23746-river-carpsucker%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'U.S. Fish & Wildlife Service',
    sourceName: 'FWS Media Library',
    sourceUrl: "https://www.fws.gov/media/river-carpsucker",
    photographer: 'Canfield, Doug/USFWS',
    rights: 'public-domain',
    rightsUrl: "https://www.fws.gov/media/river-carpsucker",
    creditText: 'Canfield, Doug/USFWS',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Carpiodes carpio. Human-free underwater whole-body lateral reference; strong scanner candidate.",
  },
  {
    id: "commons-nick-loveland-gafftopsail-123411108",
    canonicalSpeciesId: 'gafftopsail_catfish',
    scientificName: 'Bagre marinus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgafftopsail_catfish%2Fcommons-nick-loveland-gafftopsail-123411108%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgafftopsail_catfish%2Fcommons-nick-loveland-gafftopsail-123411108%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgafftopsail_catfish%2Fcommons-nick-loveland-gafftopsail-123411108%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fgafftopsail_catfish%2Fcommons-nick-loveland-gafftopsail-123411108%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'iNaturalist / Wikimedia Commons',
    sourceName: 'Wikimedia Commons (iNaturalist reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Bagre_marinus_123411108.jpg",
    photographer: 'Nick Loveland',
    rights: 'public-domain',
    rightsUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    creditText: 'Nick Loveland / Wikimedia Commons · CC0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-22',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Bagre marinus. Whole fish and human-free, but caught/on-deck context keeps it display-only.",
  },
  {
    id: "commons-vassil-clarias-batrachus-aquarium",
    canonicalSpeciesId: 'hito_catfish',
    scientificName: 'Clarias batrachus / Clarias spp.',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhito_catfish%2Fcommons-vassil-clarias-batrachus-aquarium%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhito_catfish%2Fcommons-vassil-clarias-batrachus-aquarium%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhito_catfish%2Fcommons-vassil-clarias-batrachus-aquarium%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhito_catfish%2Fcommons-vassil-clarias-batrachus-aquarium%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Clarias_batrachus_Aquarium_tropical_du_Palais_de_la_Porte_Dor%C3%A9e_10_04_2016_2.jpg",
    photographer: 'Vassil',
    rights: 'public-domain',
    rightsUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    creditText: 'Vassil / Wikimedia Commons · CC0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative image for Roddo broad Clarias batrachus / Clarias spp. profile. Exact photographed fish is Clarias batrachus. Whole fish, human-free aquarium view. Display-only because the catalog profile is broader than one exact species and the individual shows unusual pale/blotched coloration.",
  },
  {
    id: "noaa-caranx-crysos-blue-runner",
    canonicalSpeciesId: 'blue_runner',
    scientificName: 'Caranx crysos',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_runner%2Fnoaa-caranx-crysos-blue-runner%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_runner%2Fnoaa-caranx-crysos-blue-runner%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_runner%2Fnoaa-caranx-crysos-blue-runner%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fblue_runner%2Fnoaa-caranx-crysos-blue-runner%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / Wikimedia Commons',
    sourceName: 'NOAA Photo Library via Wikimedia Commons',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Caranx_crysos.jpg",
    photographer: 'SEFSC Pascagoula Laboratory; Collection of Brandi Noble, NOAA/NMFS/SEFSC',
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Caranx_crysos.jpg",
    creditText: 'NOAA/NMFS/SEFSC · Brandi Noble collection',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Caranx crysos. Whole fish, human-free, clear lateral reference. NOAA-origin public-domain status is explicitly stated on the source page. Kept display-only because this appears to be a controlled/specimen-style reference rather than a natural field view.",
  },
  {
    id: "commons-romain-guy-selene-vomer-lyon",
    canonicalSpeciesId: 'lookdown',
    scientificName: 'Selene vomer',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flookdown%2Fcommons-romain-guy-selene-vomer-lyon%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flookdown%2Fcommons-romain-guy-selene-vomer-lyon%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flookdown%2Fcommons-romain-guy-selene-vomer-lyon%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Flookdown%2Fcommons-romain-guy-selene-vomer-lyon%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons (Flickr-reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Selene_vomer_in_aquarium,_Lyon.jpg",
    photographer: 'Romain Guy',
    rights: 'public-domain',
    rightsUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    creditText: 'Romain Guy / Wikimedia Commons · CC0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Selene vomer. Whole fish, human-free aquarium profile. Strong display reference but scanner remains false because aquarium lighting and pose can distort natural appearance.",
  },
  {
    id: "commons-brian-gratwicke-kelp-bass",
    canonicalSpeciesId: 'kelp_bass',
    scientificName: 'Paralabrax clathratus',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkelp_bass%2Fcommons-brian-gratwicke-kelp-bass%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkelp_bass%2Fcommons-brian-gratwicke-kelp-bass%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkelp_bass%2Fcommons-brian-gratwicke-kelp-bass%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkelp_bass%2Fcommons-brian-gratwicke-kelp-bass%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Wikimedia Commons',
    sourceName: 'Wikimedia Commons (Flickr-reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Paralabrax_clathratus_2.jpg",
    photographer: 'Brian Gratwicke',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: 'Brian Gratwicke / Wikimedia Commons · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Paralabrax clathratus. Whole fish, human-free natural underwater view in California kelp habitat with clear diagnostic body profile. Strong scanner candidate.",
  },
  {
    id: "commons-darren-baker-pacific-barracuda",
    canonicalSpeciesId: 'pacific_barracuda',
    scientificName: 'Sphyraena argentea',
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_barracuda%2Fcommons-darren-baker-pacific-barracuda%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_barracuda%2Fcommons-darren-baker-pacific-barracuda%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_barracuda%2Fcommons-darren-baker-pacific-barracuda%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fpacific_barracuda%2Fcommons-darren-baker-pacific-barracuda%2Foriginal.webp?alt=media",
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'FishBase / Wikimedia Commons',
    sourceName: 'Wikimedia Commons (FishBase license-reviewed)',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Sphyraena_argentea.jpg",
    photographer: 'Darren Baker',
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/3.0/",
    creditText: 'Darren Baker / FishBase / Wikimedia Commons · CC BY 3.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Sphyraena argentea. Whole fish and human-free, but caught/on-deck context and low source resolution keep it display-only.",
  },
  {
    id: "commons-rudolphous-blue-mussel",
    canonicalSpeciesId: "mussel",
    scientificName: "Mytilus edulis",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmussel%2Fcommons-rudolphous-blue-mussel%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fmussel%2Fcommons-rudolphous-blue-mussel%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Wikimedia Commons / own work",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Noordwijk_-_Mossel_(Mytilus_edulis).jpg",
    photographer: "Rudolphous",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    creditText: "Rudolphous / Wikimedia Commons / CC BY-SA 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Blue Mussel reference for Roddo generic Mussel group. Whole animal, no people. Display-only because the catalog card is broader than Mytilus edulis.",
  },
  {
    id: "commons-bencostamagna-napoleon-wrasse",
    canonicalSpeciesId: "napoleon_wrasse",
    scientificName: "Cheilinus undulatus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnapoleon_wrasse%2Fcommons-bencostamagna-napoleon-wrasse%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnapoleon_wrasse%2Fcommons-bencostamagna-napoleon-wrasse%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / iNaturalist",
    sourceName: "iNaturalist via Wikimedia Commons",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Cheilinus_undulatus_449119194.jpg",
    photographer: "Ben Costamagna",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: "Ben Costamagna / iNaturalist / Wikimedia Commons / CC BY 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Cheilinus undulatus. Human-free natural underwater full-body reference.",
  },
  {
    id: "commons-rickard-zerpe-narrow-barred-mackerel",
    canonicalSpeciesId: "narrow_barred_spanish_mackerel",
    scientificName: "Scomberomorus commerson",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnarrow_barred_spanish_mackerel%2Fcommons-rickard-zerpe-narrow-barred-mackerel%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnarrow_barred_spanish_mackerel%2Fcommons-rickard-zerpe-narrow-barred-mackerel%2Fthumb.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / Flickr",
    sourceName: "Flickr via Wikimedia Commons",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Narrow-barred_spanish_mackerel_(Scomberomorus_commerson).jpg",
    photographer: "Rickard Zerpe",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: "Rickard Zerpe / Wikimedia Commons / CC BY 2.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Scomberomorus commerson. Human-free underwater full-body reference.",
  },
  {
    id: "commons-imageperson-needlefish-bahamas",
    canonicalSpeciesId: "needlefish_sea",
    scientificName: "Belonidae spp.",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fneedlefish_sea%2Fcommons-imageperson-needlefish-bahamas%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fneedlefish_sea%2Fcommons-imageperson-needlefish-bahamas%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Wikimedia Commons / own work",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Needlefish_viewed_from_above_water_in_Bahamas_0541.jpg",
    photographer: "ImagePerson",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: "ImagePerson / Wikimedia Commons / CC BY 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Belonidae group reference viewed in natural water. Human-free. Display-only because Roddo Needlefish is a family-level profile.",
  },
  {
    id: "commons-hans-hillewaert-common-squid",
    canonicalSpeciesId: "squid",
    scientificName: "Loligo vulgaris",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsquid%2Fcommons-hans-hillewaert-common-squid%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsquid%2Fcommons-hans-hillewaert-common-squid%2Fthumb.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Wikimedia Commons / own work",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Loligo_vulgaris.jpg",
    photographer: "Hans Hillewaert",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    creditText: "Hans Hillewaert / Wikimedia Commons / CC BY-SA 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Common Squid reference for Roddo generic Squid profile. Live whole specimen, no people. Display-only because catalog identity is broader.",
  },
  {
    id: "commons-brian-gratwicke-convict-surgeonfish",
    canonicalSpeciesId: "surgeonfish_sea",
    scientificName: "Acanthuridae spp.",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurgeonfish_sea%2Fcommons-brian-gratwicke-convict-surgeonfish%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurgeonfish_sea%2Fcommons-brian-gratwicke-convict-surgeonfish%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurgeonfish_sea%2Fcommons-brian-gratwicke-convict-surgeonfish%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsurgeonfish_sea%2Fcommons-brian-gratwicke-convict-surgeonfish%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / Flickr",
    sourceName: "Convict Surgeonfish, Acanthurus triostegus",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Convict_Surgeonfish,_Acanthurus_triostegus.jpg",
    photographer: "Brian Gratwicke",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: "Brian Gratwicke / Wikimedia Commons / CC BY 2.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Convict Surgeonfish (Acanthurus triostegus) for Roddo family-level Surgeonfish profile (Acanthuridae spp.). Whole human-free aquarium/reference view. Display-only because the card is broader than the photographed species.",
  },
  {
    id: "commons-diego-delso-palaemon-elegans",
    canonicalSpeciesId: "shrimp",
    scientificName: "Penaeidae / Pandalidae spp.",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshrimp%2Fcommons-diego-delso-palaemon-elegans%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshrimp%2Fcommons-diego-delso-palaemon-elegans%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshrimp%2Fcommons-diego-delso-palaemon-elegans%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fshrimp%2Fcommons-diego-delso-palaemon-elegans%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Rockpool shrimp (Palaemon elegans)",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Camar%C3%B3n_de_charco_(Palaemon_elegans),_Set%C3%BAbal,_Portugal,_2020-08-01,_DD_04.jpg",
    photographer: "Diego Delso",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    creditText: "Diego Delso / delso.photo / Wikimedia Commons / CC BY-SA 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative live rockpool shrimp in natural underwater habitat. Whole animal, no people. Display-only because Roddo Shrimp is a broad Penaeidae/Pandalidae group profile and the representative is Palaemon elegans.",
  },
  {
    id: "commons-karim-saari-octopus-vulgaris-2025",
    canonicalSpeciesId: "octopus",
    scientificName: "Octopus spp.",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foctopus%2Fcommons-karim-saari-octopus-vulgaris-2025%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foctopus%2Fcommons-karim-saari-octopus-vulgaris-2025%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foctopus%2Fcommons-karim-saari-octopus-vulgaris-2025%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foctopus%2Fcommons-karim-saari-octopus-vulgaris-2025%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Common octopus on sandy seabed",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Common_octopus_(Octopus_vulgaris)_moving_on_sandy_seabed_during_Sentinelle_4_project_-_Mus%C3%A9e_Subaquatique_de_Marseille_-_Anse_des_Catalans_-_2025.jpg",
    photographer: "Karim saari",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: "Karim saari / Wikimedia Commons / CC BY 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Common Octopus (Octopus vulgaris) for Roddo generic Octopus spp. profile. Whole live animal in natural underwater habitat with no people. Display-only because the catalog identity is broader than the photographed species.",
  },
  {
    id: "commons-avnj-hampala-macrolepidota-251680991",
    canonicalSpeciesId: "jungle_perch",
    scientificName: "Hampala macrolepidota / related spp.",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjungle_perch%2Fcommons-avnj-hampala-macrolepidota-251680991%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjungle_perch%2Fcommons-avnj-hampala-macrolepidota-251680991%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjungle_perch%2Fcommons-avnj-hampala-macrolepidota-251680991%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fjungle_perch%2Fcommons-avnj-hampala-macrolepidota-251680991%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / iNaturalist",
    sourceName: "Hampala macrolepidota 251680991",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Hampala_macrolepidota_251680991.jpg",
    photographer: "AVNJ",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/4.0/",
    creditText: "AVNJ / iNaturalist / Wikimedia Commons / CC BY 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative exact Hampala macrolepidota for Roddo broader Jungle Perch card (Hampala macrolepidota / related spp.). Whole fish, no people. Display-only because the card explicitly includes related species and this field image is caught/netted rather than scanner-grade.",
  },
  {
    id: "usfws-engbretson-largemouth-underwater-representative",
    canonicalSpeciesId: "northern_strain_largemouth",
    scientificName: "Micropterus salmoides salmoides",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_strain_largemouth%2Fusfws-engbretson-largemouth-underwater-representative%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_strain_largemouth%2Fusfws-engbretson-largemouth-underwater-representative%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_strain_largemouth%2Fusfws-engbretson-largemouth-underwater-representative%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fnorthern_strain_largemouth%2Fusfws-engbretson-largemouth-underwater-representative%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "U.S. Fish & Wildlife Service / Wikimedia Commons",
    sourceName: "Largemouth bass fish underwater Micropterus salmoides",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Largemouth_bass_fish_underwater_micropterus_salmoides.jpg",
    photographer: "Engbretson Eric, U.S. Fish and Wildlife Service",
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Largemouth_bass_fish_underwater_micropterus_salmoides.jpg",
    creditText: "Engbretson Eric / U.S. Fish & Wildlife Service",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Human-free underwater Micropterus salmoides representative for Roddo Northern-strain Largemouth Bass profile. Source does not verify northern subspecies/strain, so this remains display-only and is not scanner evidence for Micropterus salmoides salmoides.",
  },
  {
    id: 'commons-serc-red-drum-s0230',
    canonicalSpeciesId: 'redfish',
    scientificName: 'Sciaenops ocellatus',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredfish%2Fcommons-serc-red-drum-s0230%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredfish%2Fcommons-serc-red-drum-s0230%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredfish%2Fcommons-serc-red-drum-s0230%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fredfish%2Fcommons-serc-red-drum-s0230%2Foriginal.webp?alt=media',
    angle: 'side',
    lifeStage: 'adult',
    sourceAgency: 'Smithsonian Environmental Research Center',
    sourceName: 'SERC Photos via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Sciaenops_ocellatus_(S0230)_(12527956414).jpg',
    photographer: 'Robert Aguilar / Smithsonian Environmental Research Center',
    rights: 'licensed',
    rightsUrl: 'https://creativecommons.org/licenses/by/2.0/',
    creditText: 'Robert Aguilar / Smithsonian Environmental Research Center · CC BY 2.0',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Red Drum / Redfish identity. Whole fish, human-free, isolated specimen/reference framing. Display-safe; specimen-style presentation remains excluded from scanner evidence.',
  },
  {
    id: 'noaa-nurp-yellowfin-underwater',
    canonicalSpeciesId: 'yellowfin_tuna_sea',
    scientificName: 'Thunnus albacares',
    url: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_tuna_sea%2Fnoaa-nurp-yellowfin-underwater%2Fcard.webp?alt=media',
    cardUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_tuna_sea%2Fnoaa-nurp-yellowfin-underwater%2Fcard.webp?alt=media',
    thumbnailUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_tuna_sea%2Fnoaa-nurp-yellowfin-underwater%2Fthumb.webp?alt=media',
    heroUrl: 'https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fyellowfin_tuna_sea%2Fnoaa-nurp-yellowfin-underwater%2Foriginal.webp?alt=media',
    angle: 'alternate',
    lifeStage: 'adult',
    sourceAgency: 'NOAA / OAR National Undersea Research Program',
    sourceName: 'NOAA NURP via Wikimedia Commons',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Yellowfin_tuna_nurp.jpg',
    photographer: 'OAR / National Undersea Research Program (NOAA)',
    rights: 'public-domain',
    rightsUrl: 'https://commons.wikimedia.org/wiki/File:Yellowfin_tuna_nurp.jpg',
    creditText: 'NOAA / OAR National Undersea Research Program',
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: 'Exact Yellowfin Tuna identity in a human-free underwater school. Replaces the prior deck-shot primary reference; display-safe, but schooling/overlap keeps scanner eligibility conservative.',
  },
  {
    id: "noaa-lutjanus-bohar-snapper-representative",
    canonicalSpeciesId: "snapper",
    scientificName: "Lutjanus bohar",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper%2Fnoaa-lutjanus-bohar-snapper-representative%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper%2Fnoaa-lutjanus-bohar-snapper-representative%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper%2Fnoaa-lutjanus-bohar-snapper-representative%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnapper%2Fnoaa-lutjanus-bohar-snapper-representative%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "NOAA / Wikimedia Commons",
    sourceName: "Lutjanus bohar",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Lutjanus_bohar.jpg",
    photographer: "NOAA",
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Lutjanus_bohar.jpg",
    creditText: "NOAA",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative exact Lutjanus bohar reference for Roddo broad Snapper card (Lutjanus spp.). Human-free underwater whole fish. Display-only because the catalog card is broader than the photographed species.",
  },
  {
    id: "commons-bedo-channa-striata-snakehead-representative",
    canonicalSpeciesId: "snakehead",
    scientificName: "Channa striata",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnakehead%2Fcommons-bedo-channa-striata-snakehead-representative%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnakehead%2Fcommons-bedo-channa-striata-snakehead-representative%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnakehead%2Fcommons-bedo-channa-striata-snakehead-representative%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fsnakehead%2Fcommons-bedo-channa-striata-snakehead-representative%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Channa striata",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Channa_striata.jpg",
    photographer: "BEDO (Thailand)",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    creditText: "BEDO (Thailand) / Wikimedia Commons / CC BY-SA 4.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative exact Channa striata for Roddo broad Snakehead card (Channa / Parachanna spp.). Whole human-free aquarium fish. Display-only because the catalog card is broader than the photographed species.",
  },
  {
    id: "commons-james-st-john-siliqua-patula",
    canonicalSpeciesId: "razor_clam",
    scientificName: "Siliqua patula",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frazor_clam%2Fcommons-james-st-john-siliqua-patula%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frazor_clam%2Fcommons-james-st-john-siliqua-patula%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frazor_clam%2Fcommons-james-st-john-siliqua-patula%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Frazor_clam%2Fcommons-james-st-john-siliqua-patula%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / Flickr",
    sourceName: "Siliqua patula (Pacific razor clam) 1",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Siliqua_patula_(Pacific_razor_clam)_1.jpg",
    photographer: "James St. John",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: "James St. John / Wikimedia Commons / CC BY 2.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Siliqua patula museum shell reference used as a representative for Roddo broad Razor Clam card (Siliqua / Ensis spp.). Whole shell, no people. Display-only due museum context and broad catalog identity.",
  },
  {
    id: "commons-ed-bierman-red-sea-urchin",
    canonicalSpeciesId: "red_sea_urchin",
    scientificName: "Mesocentrotus franciscanus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_sea_urchin%2Fcommons-ed-bierman-red-sea-urchin%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_sea_urchin%2Fcommons-ed-bierman-red-sea-urchin%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_sea_urchin%2Fcommons-ed-bierman-red-sea-urchin%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fred_sea_urchin%2Fcommons-ed-bierman-red-sea-urchin%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / Flickr",
    sourceName: "Strongylocentrotus franciscanus at Telegraph Cove",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Strongylocentrotus_franciscanus_at_Telegraph_Cove.jpg",
    photographer: "Ed Bierman",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by/2.0/",
    creditText: "Ed Bierman / Wikimedia Commons / CC BY 2.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: true,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact red sea urchin identity (current name Mesocentrotus franciscanus; source uses Strongylocentrotus franciscanus synonym). Whole human-free natural underwater reference with clear morphology.",
  },
  {
    id: "commons-jan-johan-ter-poorten-crassostrea-gigas",
    canonicalSpeciesId: "oyster",
    scientificName: "Crassostrea gigas",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster%2Fcommons-jan-johan-ter-poorten-crassostrea-gigas%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster%2Fcommons-jan-johan-ter-poorten-crassostrea-gigas%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster%2Fcommons-jan-johan-ter-poorten-crassostrea-gigas%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Foyster%2Fcommons-jan-johan-ter-poorten-crassostrea-gigas%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Crassostrea gigas",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Crassostrea_gigas.jpg",
    photographer: "Jan Johan ter Poorten",
    rights: 'licensed',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:Crassostrea_gigas.jpg",
    creditText: "Jan Johan ter Poorten / Wikimedia Commons",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative Pacific oyster (Crassostrea gigas) for Roddo broad Oyster card (Crassostrea / Ostrea spp.). Whole shell, no people. Source page documents GFDL/VRT permission; display-only because catalog identity is broader.",
  },
  {
    id: "public-domain-megalops-cyprinoides-thomas",
    canonicalSpeciesId: "tarpon_indo_pacific",
    scientificName: "Megalops cyprinoides",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon_indo_pacific%2Fpublic-domain-megalops-cyprinoides-thomas%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon_indo_pacific%2Fpublic-domain-megalops-cyprinoides-thomas%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon_indo_pacific%2Fpublic-domain-megalops-cyprinoides-thomas%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Ftarpon_indo_pacific%2Fpublic-domain-megalops-cyprinoides-thomas%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / The Rod in India",
    sourceName: "MegalopsCyprinoides",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:MegalopsCyprinoides.jpg",
    photographer: "Henry Sullivan Thomas (historical illustration)",
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:MegalopsCyprinoides.jpg",
    creditText: "Henry Sullivan Thomas / public domain",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Megalops cyprinoides historical whole-fish illustration. Human-free and rights-safe, but artwork context keeps it display-only for scanner gating.",
  },
  {
    id: "commons-opencage-sebastes-schlegelii",
    canonicalSpeciesId: "korean_rockfish",
    scientificName: "Sebastes schlegelii",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkorean_rockfish%2Fcommons-opencage-sebastes-schlegelii%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkorean_rockfish%2Fcommons-opencage-sebastes-schlegelii%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkorean_rockfish%2Fcommons-opencage-sebastes-schlegelii%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fkorean_rockfish%2Fcommons-opencage-sebastes-schlegelii%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / OpenCage",
    sourceName: "Sebastes schlegelii by OpenCage",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Sebastes_schlegelii_by_OpenCage.jpg",
    photographer: "OpenCage",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/2.5/",
    creditText: "OpenCage / Wikimedia Commons / CC BY-SA 2.5",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Korean Rockfish (Sebastes schlegelii), whole human-free aquarium reference. Display-only because of aquarium angle/context.",
  },
  {
    id: "commons-mercenaria-mercenaria-hard-clam",
    canonicalSpeciesId: "hard_clam",
    scientificName: "Mercenaria mercenaria",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhard_clam%2Fcommons-mercenaria-mercenaria-hard-clam%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhard_clam%2Fcommons-mercenaria-mercenaria-hard-clam%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhard_clam%2Fcommons-mercenaria-mercenaria-hard-clam%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Fhard_clam%2Fcommons-mercenaria-mercenaria-hard-clam%2Foriginal.webp?alt=media",
    angle: "alternate",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons",
    sourceName: "Mercenaria mercenaria",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Mercenaria_mercenaria.jpg",
    photographer: "Anonymous Powered / self-photographed source",
    rights: 'licensed',
    rightsUrl: "https://creativecommons.org/licenses/by-sa/3.0/",
    creditText: "Wikimedia Commons / CC BY-SA 3.0",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Exact Hard Clam (Mercenaria mercenaria) whole-shell group reference. Human-free. Display-only because multiple specimens overlap and scene is not scanner-grade.",
  },
  {
    id: "public-domain-francis-day-lethrinus-nebulosus",
    canonicalSpeciesId: "emperor_fish",
    scientificName: "Lethrinus nebulosus",
    url: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Femperor_fish%2Fpublic-domain-francis-day-lethrinus-nebulosus%2Fcard.webp?alt=media",
    cardUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Femperor_fish%2Fpublic-domain-francis-day-lethrinus-nebulosus%2Fcard.webp?alt=media",
    thumbnailUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Femperor_fish%2Fpublic-domain-francis-day-lethrinus-nebulosus%2Fthumb.webp?alt=media",
    heroUrl: "https://firebasestorage.googleapis.com/v0/b/roddo-official.firebasestorage.app/o/fishIQReference%2Fspecies%2Femperor_fish%2Fpublic-domain-francis-day-lethrinus-nebulosus%2Foriginal.webp?alt=media",
    angle: "side",
    lifeStage: "adult",
    sourceAgency: "Wikimedia Commons / Fauna of British India",
    sourceName: "LethrinusNebulosus",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:LethrinusNebulosus.png",
    photographer: "Sir Francis Day (historical illustration)",
    rights: 'public-domain',
    rightsUrl: "https://commons.wikimedia.org/wiki/File:LethrinusNebulosus.png",
    creditText: "Sir Francis Day / public domain",
    speciesVerified: true,
    rightsVerified: true,
    verified: true,
    scannerEligible: false,
    reviewedAt: '2026-08-23',
    reviewedBy: 'Roddo visual reference audit',
    notes: "Representative exact Lethrinus nebulosus for Roddo broad Emperor Fish card (Lethrinus spp.). Whole fish illustration, no people. Display-only because catalog identity is broader and source is artwork.",
  },
];
